import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';
import { AnimatePresence, LayoutGroup, motion, useReducedMotion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Check, FileText, X } from 'lucide-react';
import Carousel3D from '../subscriptions/Carousel3D';
import {
  previewCurrentClientServiceChange,
  type CurrentServicePreviewResponse,
  type ServiceCatalogOffering,
  type ServiceCatalogService,
  type ServiceCatalogTier,
  type ServiceWorkspaceCurrentService,
} from '../../lib/clientServices';
import {
  getSkuById,
  PRIMARY_SERVICE_CATEGORIES,
  type ServiceCategory,
} from '../../lib/pricebook/pricebook';
import {
  calcOrder,
  roundPercentCents,
  type CalculatedOrder,
  type CartItemInput,
} from '../../lib/pricing/calcOrder';
import {
  canSelectLane,
  getLaneRuleForTier,
  readServiceLaneOptions,
  sanitizeLaneSelection,
} from '../../lib/services/managedMarketingLaneRules';
import type { AppDispatch, RootState } from '../../store';
import { fetchPaymentMethods } from '../../store/features/billing/billingSlice';
import { gradientPillCss } from '../../styles/gradientPill';
import type { ServiceConfigSubmission } from './ServiceConfigDrawer';

export type ClientServiceExplorerSelectionSeed = {
  key: string;
  proposalId?: string | null;
  serviceCode?: string | null;
  serviceCategory?: string | null;
  tierSkuId?: string | null;
  offeringCodes?: string[];
  offeringLabels?: string[];
  discountCode?: string | null;
};

type Props = {
  orgId: string;
  services: ServiceCatalogService[];
  currentServices: ServiceWorkspaceCurrentService[];
  canManageServices: boolean;
  actionMode?: 'checkout' | 'proposal';
  checkoutBusy?: boolean;
  selectionSeed?: ClientServiceExplorerSelectionSeed | null;
  onCheckout: (payload: ServiceConfigSubmission) => Promise<void> | void;
};

type StepKey = 'tier' | 'offerings' | 'discount';
type ServiceAction =
  | 'purchase'
  | 'update'
  | 'upgrade'
  | 'no_change'
  | 'downgrade_blocked';

type Theme = { gradient: string; footer: string; glow: string };

type ExplorerSku = {
  skuId: string;
  name: string;
  billingType: 'recurring' | 'one_time';
  interval?: 'month';
  amountCents: number;
  serviceCategory: ServiceCategory;
  isPrimaryService: boolean;
};

type MobileOfferingDrawerState = {
  selectionValue: string;
  offering: ServiceCatalogOffering;
  eyebrow: string;
  kindLabel: 'service lane' | 'offering';
};

const Shell = styled.section`
  position: relative;
  min-height: min(84vh, 920px);
`;

const Viewport = styled.div`
  position: relative;
  min-height: min(84vh, 920px);
`;

const DeckView = styled(motion.div)`
  display: grid;
  gap: 18px;
`;

const DetailView = styled(motion.div)`
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(260px, 320px);
  gap: clamp(24px, 3vw, 40px);
  align-items: start;
  min-height: min(84vh, 920px);
  padding: 12px 4px 24px;

  @media (max-width: 1080px) {
    grid-template-columns: 1fr;
  }
`;

const Main = styled.div`
  display: grid;
  gap: 22px;
  align-content: start;
  padding-top: 18px;
`;

const Rail = styled.div`
  display: grid;
  justify-items: end;
  align-content: start;
  gap: 14px;
  padding-top: 22px;
  align-self: start;
  position: sticky;
  top: 88px;
  height: fit-content;

  @media (max-width: 1080px) {
    order: -1;
    justify-items: center;
    position: static;
    height: auto;
    padding-top: 0;
  }

  @media (max-width: 760px) {
    display: none;
  }
`;

const RailCardWrap = styled.div`
  width: min(100%, 320px);

  @media (max-width: 1080px) {
    width: 100%;
    display: grid;
    justify-items: center;
  }
`;

const BackButton = styled.button`
  min-height: 34px;
  width: fit-content;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid rgba(255,255,255,.14);
  background: rgba(255,255,255,.04);
  color: #f3f4f6;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: .18px;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  cursor: pointer;
`;

const Eyebrow = styled.div`
  color: #8d96a8;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: .26px;
  text-transform: uppercase;
`;

const Title = styled.h2`
  margin: 0;
  color: #f3f4f6;
  font-size: clamp(2.8rem, 6vw, 4.25rem);
  line-height: .94;
  font-weight: 900;
  letter-spacing: -.04em;
  max-width: 10ch;
`;

const Copy = styled.p`
  margin: 0;
  color: #c2cad8;
  font-size: 14px;
  line-height: 1.7;
  font-weight: 700;
  max-width: 70ch;
`;

const StepText = styled.div`
  display: grid;
  gap: 6px;
`;

const StepKicker = styled.div`
  color: #8d96a8;
  font-size: 10px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: .24px;
`;

const StepTitle = styled.div`
  color: #f3f4f6;
  font-size: 18px;
  font-weight: 900;
`;

const StepCopy = styled.div`
  color: #b7c0cf;
  font-size: 12px;
  line-height: 1.6;
  font-weight: 700;
`;

const FlatStep = styled(motion.div)<{ $dimmed: boolean }>`
  display: grid;
  gap: 14px;
`;

const TierRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: stretch;
  overflow-x: auto;
  padding-bottom: 4px;

  @media (max-width: 960px) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    overflow-x: visible;
    padding-bottom: 0;
  }

  @media (max-width: 760px) {
    display: flex;
    overflow-x: auto;
    overflow-y: hidden;
    padding-bottom: 14px;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior-x: contain;
  }
`;

const Option = styled.button<{ $active: boolean }>`
  flex: 0 0 clamp(156px, 15vw, 188px);
  width: auto;
  aspect-ratio: 0.9;
  text-align: left;
  border-radius: 18px;
  border: 1px dashed
    ${({ $active }) => ($active ? 'rgba(224,161,94,.58)' : 'rgba(255,255,255,.18)')};
  background: transparent;
  padding: 13px 14px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  color: inherit;
  cursor: pointer;
  transition: border-color 140ms ease;

  &:hover {
    border-color: ${({ $active }) =>
      $active ? 'rgba(224,161,94,.72)' : 'rgba(255,255,255,.3)'};
  }

  @media (max-width: 960px) {
    flex: initial;
    width: 100%;
  }

  @media (max-width: 760px) {
    flex: 0 0 min(58vw, 184px);
    width: min(58vw, 184px);
    aspect-ratio: 0.76;
  }
`;

const OptionFooterStack = styled.div`
  margin-top: auto;
  padding-top: 10px;
  display: grid;
  gap: 4px;
`;

const OptionFooter = styled.div`
  color: #9faabc;
  font-size: 11px;
  line-height: 1.45;
  font-weight: 800;
`;

const Selection = styled.button<{ $active: boolean }>`
  width: 100%;
  text-align: left;
  border-radius: 16px;
  border: 1px dashed
    ${({ $active }) => ($active ? 'rgba(224,161,94,.58)' : 'rgba(255,255,255,.18)')};
  background: transparent;
  padding: 12px 14px;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 10px;
  color: inherit;
  cursor: pointer;
  transition: border-color 140ms ease;

  &:hover {
    border-color: ${({ $active }) =>
      $active ? 'rgba(224,161,94,.72)' : 'rgba(255,255,255,.3)'};
  }

  @media (max-width: 760px) {
    min-height: 104px;
    padding: 12px;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: start;
  }
`;

const SelectionGrid = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 720px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const SelectionIndicator = styled.div<{ $active: boolean }>`
  width: 18px;
  height: 18px;
  border-radius: 999px;
  border: 1px solid
    ${({ $active }) =>
      $active ? 'rgba(86,211,173,.72)' : 'rgba(255,255,255,.22)'};
  background: ${({ $active }) =>
    $active ? 'rgba(86,211,173,.18)' : 'transparent'};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;

  @media (max-width: 760px) {
    margin-top: 0;
    order: 2;
  }
`;

const SelectionBody = styled.div`
  min-width: 0;
  display: grid;
  gap: 4px;

  @media (max-width: 760px) {
    order: 1;
    gap: 6px;
    align-content: start;
  }
`;

const SelectionTitle = styled.div`
  color: #f3f4f6;
  font-size: 13px;
  font-weight: 900;
  max-width: 100%;

  @media (max-width: 760px) {
    font-size: 10px;
    line-height: 1.3;
    overflow: hidden;
    overflow-wrap: anywhere;
    word-break: break-word;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 4;
  }
`;

const SelectionDescription = styled.div`
  color: #9faabc;
  font-size: 11px;
  line-height: 1.55;
  font-weight: 700;

  @media (max-width: 760px) {
    display: none;
  }
`;

const OfferingTypeSection = styled.section`
  display: grid;
  gap: 10px;
`;

const OfferingTypeHeading = styled.h3`
  margin: 0;
  color: #9ca6b6;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.16em;
`;

const OfferingMeta = styled.div`
  color: #9ca6b6;
  font-size: 10px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.12em;

  @media (max-width: 760px) {
    font-size: 8px;
    letter-spacing: 0.14em;
  }
`;

const Input = styled.input`
  width: 100%;
  min-height: 44px;
  border-radius: 14px;
  border: 1px solid rgba(255,255,255,.1);
  background: rgba(255,255,255,.03);
  color: #f3f4f6;
  padding: 0 14px;
  font-size: 13px;
  font-weight: 800;
  box-sizing: border-box;
`;

const PrimaryButton = styled.button`
  min-height: 40px;
  padding: 0 16px;
  border-radius: 999px;
  border: 1px solid rgba(224,161,94,.38);
  background: rgba(224,161,94,.14);
  color: #f3f4f6;
  font-size: 12px;
  font-weight: 900;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;

  &:disabled {
    opacity: .44;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  min-height: 34px;
  width: fit-content;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid rgba(255,255,255,.12);
  background: rgba(255,255,255,.04);
  color: #dbe3ef;
  font-size: 11px;
  font-weight: 900;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  cursor: pointer;
`;

const RailPurchaseWrap = styled(motion.div)`
  width: min(100%, 320px);
  display: grid;
  gap: 10px;

  @media (max-width: 1080px) {
    width: 100%;
    justify-items: center;
  }
`;

const RailPurchaseButton = styled(PrimaryButton)`
  width: 100%;
  min-height: 48px;
  justify-content: center;
  padding: 0 18px;
`;

const RailPurchaseMeta = styled.div`
  color: #9ca6b6;
  font-size: 11px;
  line-height: 1.55;
  font-weight: 700;
  text-align: center;
`;

const MobileSnapshotPill = styled.button<{ $theme: Theme }>`
  position: fixed;
  right: 16px;
  bottom: max(16px, calc(env(safe-area-inset-bottom, 0px) + 10px));
  min-height: 44px;
  padding: 0 14px;
  border: 0;
  border-radius: 999px;
  background: ${({ $theme }) => $theme.gradient};
  color: #fff;
  box-shadow: ${({ $theme }) => $theme.glow};
  display: none;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: .12em;
  text-transform: uppercase;
  z-index: 1105;
  cursor: pointer;

  @media (max-width: 760px) {
    display: inline-flex;
  }
`;

const MobileSnapshotOverlay = styled(motion.button)`
  position: fixed;
  inset: 0;
  border: 0;
  padding: 0;
  background: rgba(7, 10, 16, 0.58);
  backdrop-filter: blur(8px);
  z-index: 1190;
  cursor: pointer;
  display: none;

  @media (max-width: 760px) {
    display: block;
  }
`;

const MobileSnapshotDrawer = styled(motion.aside)`
  position: fixed;
  top: 0;
  right: 0;
  width: min(90vw, 360px);
  height: 100dvh;
  background: linear-gradient(180deg, rgba(20,22,28,.98), rgba(16,18,24,.98));
  box-shadow: 0 24px 90px rgba(0,0,0,.44);
  z-index: 1191;
  display: none;
  grid-template-rows: auto minmax(0, 1fr);

  @supports not (height: 100dvh) {
    height: 100vh;
  }

  @media (max-width: 760px) {
    display: grid;
  }
`;

const MobileSnapshotDrawerHeader = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 64px;
  padding: 16px 56px 14px;
`;

const MobileSnapshotDrawerTitle = styled.div`
  color: #f3f4f6;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: .18em;
  text-transform: uppercase;
  text-align: center;
`;

const MobileSnapshotDrawerClose = styled.button`
  position: absolute;
  top: 50%;
  right: 16px;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 999px;
  background: rgba(255,255,255,.05);
  color: #e8eaed;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const MobileSnapshotDrawerScroll = styled.div`
  overflow-y: auto;
  min-height: 0;
  padding: 16px;
  display: grid;
  gap: 14px;
  align-content: start;
`;

const MobileSnapshotCardWrap = styled.div`
  width: 100%;
  display: grid;
  justify-items: center;

  & > * {
    width: 100%;
  }
`;

const MobileOfferingOverlay = styled(motion.button)`
  position: fixed;
  inset: 0;
  border: 0;
  padding: 0;
  background: rgba(7, 10, 16, 0.58);
  backdrop-filter: blur(8px);
  z-index: 1192;
  cursor: pointer;
  display: none;

  @media (max-width: 760px) {
    display: block;
  }
`;

const MobileOfferingDrawer = styled(motion.aside)`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  max-height: min(76dvh, 560px);
  background: linear-gradient(180deg, rgba(20,22,28,.98), rgba(16,18,24,.98));
  border-top: 1px solid rgba(255,255,255,.08);
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
  box-shadow: 0 -24px 90px rgba(0,0,0,.42);
  z-index: 1193;
  display: none;
  grid-template-rows: auto minmax(0, 1fr);
  overflow: hidden;

  @supports not (max-height: 76dvh) {
    max-height: 560px;
  }

  @media (max-width: 760px) {
    display: grid;
  }
`;

const MobileOfferingDrawerHeader = styled.div`
  display: grid;
  gap: 10px;
  padding: 10px 16px 14px;
  border-bottom: 1px solid rgba(255,255,255,.08);
`;

const MobileOfferingDrawerHandle = styled.div`
  width: 42px;
  height: 4px;
  border-radius: 999px;
  background: rgba(255,255,255,.18);
  justify-self: center;
`;

const MobileOfferingDrawerTopRow = styled.div`
  display: block;
`;

const MobileOfferingDrawerHeaderMeta = styled.div`
  min-width: 0;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  column-gap: 8px;
  row-gap: 4px;
  align-items: start;
`;

const MobileOfferingDrawerScroll = styled.div`
  overflow-y: auto;
  min-height: 0;
  padding: 16px;
  padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 20px);
  display: grid;
  gap: 16px;
  align-content: start;
`;

const MobileOfferingDrawerSummary = styled.div`
  display: grid;
  gap: 12px;
`;

const MobileOfferingDrawerIndicator = styled(SelectionIndicator)`
  margin-top: 0;
  order: initial;
  grid-column: 1;
  grid-row: 1 / span 2;
`;

const MobileOfferingDrawerEyebrow = styled(OfferingMeta)`
  grid-column: 2;
  grid-row: 1;
`;

const MobileOfferingDrawerTitle = styled.div`
  color: #f3f4f6;
  font-size: 1.05rem;
  line-height: 1.2;
  font-weight: 900;
`;

const MobileOfferingDrawerStatus = styled.div`
  color: #9ca6b6;
  font-size: 11px;
  line-height: 1.55;
  font-weight: 800;
  grid-column: 2;
  grid-row: 2;
`;

const MobileOfferingDrawerCopy = styled.div`
  color: #b9c2d0;
  font-size: 13px;
  line-height: 1.65;
  font-weight: 700;
`;

const MobileOfferingActionButton = styled.button`
  ${gradientPillCss};
  width: 100%;
  min-height: 46px;
  border: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const Notice = styled.div<{ $tone?: 'warn' | 'good' | 'neutral' }>`
  border-radius: 16px;
  padding: 13px 14px;
  border: 1px solid ${({ $tone = 'neutral' }) =>
    $tone === 'warn' ? 'rgba(224,161,94,.24)' : $tone === 'good' ? 'rgba(86,211,173,.24)' : 'rgba(255,255,255,.08)'};
  background: ${({ $tone = 'neutral' }) =>
    $tone === 'warn' ? 'rgba(224,161,94,.08)' : $tone === 'good' ? 'rgba(86,211,173,.08)' : 'rgba(255,255,255,.03)'};
  color: #d5dde8;
  font-size: 12px;
  line-height: 1.6;
  font-weight: 700;
`;

const ServiceBanner = styled.div<{ $theme: Theme }>`
  border-radius: 18px;
  padding: 14px 16px;
  background: ${({ $theme }) => $theme.gradient};
  color: #fff;
  box-shadow: ${({ $theme }) => $theme.glow};
  display: grid;
  gap: 4px;
`;

const CardButton = styled(motion.button)<{ $mode: 'deck' | 'detail'; $theme: Theme }>`
  width: ${({ $mode }) => ($mode === 'deck' ? '100%' : 'min(100%, 320px)')};
  height: ${({ $mode }) => ($mode === 'deck' ? '100%' : 'auto')};
  min-height: ${({ $mode }) => ($mode === 'detail' ? '600px' : '0')};
  text-align: left;
  border: 0;
  padding: 0;
  border-radius: 28px;
  overflow: hidden;
  cursor: pointer;
  color: #fff;
  position: relative;
  background: ${({ $theme }) => $theme.gradient};
  box-shadow: ${({ $theme }) => $theme.glow};
  display: flex;
  flex-direction: column;

  @media (max-width: 760px) {
    border-radius: 22px;
    min-height: ${({ $mode }) => ($mode === 'detail' ? '500px' : '0')};
  }
`;

const CardBody = styled(motion.div)`
  position: relative;
  z-index: 1;
  display: grid;
  gap: 14px;
  align-content: start;
  padding: 22px 22px 0;
  flex: 1 1 auto;

  @media (max-width: 760px) {
    gap: 10px;
    padding: 16px 16px 0;
  }
`;

const CardFooter = styled.div<{ $theme: Theme }>`
  position: relative;
  z-index: 1;
  display: grid;
  gap: 8px;
  padding: 18px 22px 22px;
  background: ${({ $theme }) => $theme.footer};
  backdrop-filter: blur(10px);
  border-bottom-left-radius: 28px;
  border-bottom-right-radius: 28px;
  margin-top: auto;

  @media (max-width: 760px) {
    gap: 6px;
    padding: 14px 16px 16px;
    border-bottom-left-radius: 22px;
    border-bottom-right-radius: 22px;
  }
`;

const CardEyebrow = styled.div`
  color: rgba(255,255,255,.86);
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: .22px;

  @media (max-width: 760px) {
    font-size: 9px;
  }
`;

const CardCopy = styled.div`
  color: rgba(255,255,255,.92);
  font-size: 14px;
  line-height: 1.55;
  font-weight: 700;
  width: 100%;

  @media (max-width: 760px) {
    font-size: 12px;
    line-height: 1.45;
  }
`;

const ActivePlanDetails = styled.div`
  display: grid;
  gap: 10px;
  width: 100%;
`;

const ActivePlanList = styled.ul`
  margin: 0;
  padding-left: 18px;
  display: grid;
  gap: 6px;
  color: rgba(255,255,255,.94);
  font-size: 12px;
  line-height: 1.5;
  font-weight: 800;

  @media (max-width: 760px) {
    display: none;
  }
`;

const ActivePlanText = styled.div`
  color: rgba(255,255,255,.94);
  font-size: 12px;
  line-height: 1.5;
  font-weight: 800;

  @media (max-width: 760px) {
    display: none;
  }
`;

const ActivePlanHours = styled.div`
  color: rgba(255,255,255,.76);
  font-size: 11px;
  line-height: 1.5;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: .16px;

  @media (max-width: 760px) {
    font-size: 9px;
  }
`;

const CardStatus = styled.span<{ $current?: boolean }>`
  min-height: 26px;
  padding: 0 10px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  border: 1px solid rgba(255,255,255,.28);
  background: ${({ $current }) => ($current ? 'rgba(255,255,255,.2)' : 'rgba(255,255,255,.12)')};
  color: #fff;
  font-size: 10px;
  font-weight: 900;
  text-transform: uppercase;

  @media (max-width: 760px) {
    display: none;
  }
`;

const SnapshotStack = styled.div`
  display: grid;
  gap: 12px;

  @media (max-width: 760px) {
    gap: 10px;
  }
`;

const SnapshotItem = styled.div`
  display: grid;
  gap: 4px;
`;

const SnapshotLabel = styled.div`
  color: rgba(255,255,255,.72);
  font-size: 10px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: .2px;

  @media (max-width: 760px) {
    font-size: 9px;
  }
`;

const SnapshotValue = styled.div`
  color: #fff;
  font-size: 13px;
  line-height: 1.5;
  font-weight: 800;

  @media (max-width: 760px) {
    font-size: 11px;
    line-height: 1.4;
  }
`;

const DeckServiceTitle = styled.div`
  margin-top: 6px;
  font-size: clamp(2.1rem, 5vw, 3.2rem);
  line-height: .96;
  font-weight: 900;
  letter-spacing: -.04em;
  max-width: 8ch;

  @media (max-width: 760px) {
    margin-top: 2px;
    font-size: clamp(1.45rem, 7.4vw, 2rem);
    max-width: 9ch;
  }
`;

const DetailServiceTitle = styled.div`
  margin-top: 2px;
  font-size: clamp(1.55rem, 3vw, 2.15rem);
  line-height: .94;
  font-weight: 900;
  letter-spacing: -.04em;
  max-width: 9ch;

  @media (max-width: 760px) {
    font-size: clamp(1.2rem, 6vw, 1.55rem);
    max-width: 10ch;
  }
`;

const FooterPriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
`;

const FooterPrimaryValue = styled.div`
  color: #fff;
  font-size: clamp(2.2rem, 4.3vw, 3rem);
  line-height: .95;
  font-weight: 900;
  letter-spacing: -.03em;

  @media (max-width: 760px) {
    font-size: clamp(1.5rem, 7vw, 2rem);
  }
`;

const FooterPrimaryMeta = styled.div`
  color: rgba(255,255,255,.88);
  font-size: 14px;
  font-weight: 800;

  @media (max-width: 760px) {
    font-size: 11px;
  }
`;

const FooterSupportText = styled.div`
  color: rgba(255,255,255,.92);
  font-size: 12px;
  line-height: 1.5;
  font-weight: 700;

  @media (max-width: 760px) {
    font-size: 10px;
    line-height: 1.4;
  }
`;

const ModalBackdrop = styled(motion.button)`
  position: fixed;
  inset: 0;
  border: 0;
  padding: 0;
  background: rgba(7, 10, 16, 0.72);
  backdrop-filter: blur(10px);
  z-index: 1200;
  cursor: pointer;
`;

const ModalViewport = styled.div`
  position: fixed;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 24px;
  z-index: 1201;
  pointer-events: none;
`;

const ModalPanel = styled(motion.section)`
  position: relative;
  width: min(94vw, 860px);
  max-height: min(86vh, 760px);
  overflow-y: auto;
  border-radius: 22px;
  border: 1px solid rgba(255,255,255,.08);
  background: linear-gradient(180deg, rgba(20,22,28,.98), rgba(16,18,24,.98));
  box-shadow: 0 24px 90px rgba(0,0,0,.44);
  padding: 22px;
  display: grid;
  gap: 18px;
  pointer-events: auto;
`;

const ModalHeader = styled.div`
  display: grid;
  gap: 10px;
`;

const ModalHeaderText = styled.div`
  display: grid;
  gap: 6px;
`;

const ModalKicker = styled.div`
  color: #f6c37e;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.18em;
  text-transform: uppercase;
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: #f3f4f6;
  font-size: clamp(1.2rem, 2vw, 1.45rem);
  line-height: 1.05;
  font-weight: 900;
`;

const ModalSub = styled.p`
  margin: 0;
  color: #b7c0cf;
  font-size: 12px;
  line-height: 1.6;
  font-weight: 700;
  max-width: 54ch;
`;

const ModalGrid = styled.div<{ $singleColumn?: boolean }>`
  display: grid;
  gap: 18px;
  grid-template-columns: ${({ $singleColumn }) =>
    $singleColumn ? 'minmax(0, 1fr)' : 'minmax(0, 1.08fr) minmax(300px, .92fr)'};
  align-items: start;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const PurchaseBlockTitle = styled.div`
  color: #f3f4f6;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: .2px;
  text-transform: uppercase;
`;

const Receipt = styled.div`
  display: grid;
  gap: 18px;
  align-content: start;
`;

const PurchaseDetails = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const PurchaseCell = styled.div`
  display: grid;
  gap: 6px;
  padding-bottom: 14px;
  border-bottom: 1px solid rgba(255,255,255,.08);
`;

const PurchaseWideCell = styled(PurchaseCell)`
  grid-column: 1 / -1;
`;

const PurchaseLabel = styled.div`
  color: #8d96a8;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: .18px;
  text-transform: uppercase;
`;

const PurchaseValue = styled.div`
  color: #f3f4f6;
  font-size: 13px;
  line-height: 1.55;
  font-weight: 800;
`;

const OfferingList = styled.ul`
  margin: 0;
  padding-left: 18px;
  display: grid;
  gap: 6px;
`;

const OfferingListItem = styled.li`
  color: #f3f4f6;
  font-size: 13px;
  line-height: 1.6;
  font-weight: 800;
`;

const EstimateHero = styled.div`
  display: grid;
  gap: 20px;
  padding-bottom: 18px;
  border-bottom: 1px solid rgba(255,255,255,.08);

  @media (min-width: 760px) {
    grid-template-columns: minmax(0, 1.1fr) minmax(220px, .9fr);
    align-items: start;
  }
`;

const EstimateHeroLabel = styled.div`
  color: rgba(255,255,255,.72);
  font-size: 10px;
  font-weight: 900;
  letter-spacing: .18px;
  text-transform: uppercase;
`;

const EstimateHeroValue = styled.div`
  color: #f3f4f6;
  font-size: clamp(2rem, 4vw, 2.75rem);
  line-height: .92;
  font-weight: 900;
  letter-spacing: -.04em;
`;

const typingLoop = keyframes`
  0%,
  12% {
    width: 0ch;
  }
  62%,
  78% {
    width: 14ch;
  }
  100% {
    width: 0ch;
  }
`;

const caretBlink = keyframes`
  0%,
  49% {
    border-right-color: rgba(243,244,246,.92);
  }
  50%,
  100% {
    border-right-color: transparent;
  }
`;

const TypingEstimateHeroValue = styled(EstimateHeroValue)<{ $reducedMotion?: boolean }>`
  display: inline-block;
  width: 14ch;
  white-space: nowrap;
  overflow: hidden;
  border-right: 2px solid rgba(243,244,246,.92);
  padding-right: 2px;

  ${({ $reducedMotion }) =>
    $reducedMotion
      ? css`
    width: auto;
    border-right-color: transparent;
  `
      : css`
    animation: ${typingLoop} 1.9s steps(14, end) infinite, ${caretBlink} .85s step-end infinite;
  `}
`;

const EstimateHeroCopy = styled.div`
  color: #d5dde8;
  font-size: 12px;
  line-height: 1.6;
  font-weight: 700;
  max-width: 56ch;
`;

const EstimateHeroLead = styled.div`
  display: grid;
  gap: 10px;
`;

const EstimateHeroMetrics = styled.div`
  display: grid;
  gap: 10px;
  align-content: start;
`;

const EstimateHeroMetric = styled.div`
  display: grid;
  gap: 4px;
`;

const EstimateHeroMetricLabel = styled.div`
  color: rgba(255,255,255,.66);
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.16em;
  text-transform: uppercase;
`;

const EstimateHeroMetricValue = styled.div`
  color: #f3f4f6;
  font-size: 15px;
  line-height: 1.35;
  font-weight: 900;
`;

const PaymentCard = styled.div`
  display: grid;
  gap: 16px;
`;

const PaymentVisual = styled.div`
  position: relative;
  width: min(100%, 360px);
  aspect-ratio: 1.586 / 1;
  min-height: 0;
  box-sizing: border-box;
  align-self: start;
  border-radius: 22px;
  overflow: hidden;
  padding: 18px 18px 16px;
  border: 1px solid rgba(255,255,255,.12);
  background:
    radial-gradient(circle at top right, rgba(255,255,255,.14), transparent 34%),
    radial-gradient(circle at bottom left, rgba(255,255,255,.08), transparent 30%),
    linear-gradient(145deg, rgba(46,50,66,.98), rgba(20,24,34,.98));
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,.08),
    0 18px 40px rgba(0,0,0,.28);
  display: grid;
  align-content: space-between;
  gap: 18px;
`;

const PaymentVisualGlow = styled.div`
  position: absolute;
  inset: auto -12% -28% auto;
  width: 180px;
  height: 180px;
  border-radius: 999px;
  background: rgba(224,161,94,.16);
  filter: blur(28px);
  pointer-events: none;
`;

const PaymentVisualTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  gap: 12px;
`;

const PaymentStatus = styled.div`
  min-height: 26px;
  padding: 0 10px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255,255,255,.18);
  background: rgba(255,255,255,.08);
  color: #f3f4f6;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: .2px;
  text-transform: uppercase;
`;

const PaymentBrand = styled.div`
  color: rgba(255,255,255,.96);
  font-size: 14px;
  font-weight: 900;
  letter-spacing: .18px;
`;

const PaymentDigits = styled.div`
  color: #fff;
  font-size: clamp(1.05rem, 2vw, 1.28rem);
  line-height: 1;
  font-weight: 900;
  letter-spacing: .06em;
  white-space: nowrap;
  display: inline-flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: nowrap;
`;

const PaymentVisualMeta = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
`;

const PaymentMetaItem = styled.div`
  display: grid;
  gap: 4px;
`;

const PaymentMetaLabel = styled.div`
  color: rgba(255,255,255,.62);
  font-size: 9px;
  font-weight: 900;
  letter-spacing: .22px;
  text-transform: uppercase;
`;

const PaymentMetaValue = styled.div`
  color: rgba(255,255,255,.94);
  font-size: 12px;
  font-weight: 800;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding-top: 16px;
  border-top: 1px solid rgba(255,255,255,.08);
`;

const ModalActionMeta = styled.div`
  color: #9ca6b6;
  font-size: 12px;
  line-height: 1.55;
  font-weight: 700;
  max-width: 44ch;
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const ModalStateWrap = styled(motion.div)`
  display: grid;
  gap: 18px;
  min-height: 320px;
  align-content: center;
  justify-items: start;
  padding: 12px 0;
`;

const ModalStateIcon = styled.div<{ $success?: boolean }>`
  width: 58px;
  height: 58px;
  border-radius: 999px;
  border: 1px solid
    ${({ $success }) =>
      $success ? 'rgba(86,211,173,.34)' : 'rgba(224,161,94,.32)'};
  background: ${({ $success }) =>
    $success ? 'rgba(86,211,173,.12)' : 'rgba(224,161,94,.1)'};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #f3f4f6;
`;

const ModalSpinner = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 999px;
  border: 2px solid rgba(255,255,255,.16);
  border-top-color: rgba(224,161,94,.9);
  animation: ${spin} 0.9s linear infinite;
`;

const ModalStateTitle = styled.h4`
  margin: 0;
  color: #f3f4f6;
  font-size: clamp(1.35rem, 2vw, 1.65rem);
  line-height: 1.05;
  font-weight: 900;
`;

const ModalStateCopy = styled.p`
  margin: 0;
  color: #c3cbd8;
  font-size: 13px;
  line-height: 1.7;
  font-weight: 700;
  max-width: 52ch;
`;

function cleanText(value: unknown): string { return typeof value === 'string' ? value.trim() : ''; }
function normalizeCode(value: string | null | undefined) { return cleanText(value).toLowerCase(); }
function formatPaymentBrand(brand: string | null | undefined) {
  const normalized = cleanText(brand);
  if (!normalized) return 'Card';
  return normalized
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}
function formatCurrency(cents: number, currency = 'usd') {
  const amount = Number.isFinite(cents) ? cents / 100 : 0;
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency.toUpperCase(), maximumFractionDigits: amount % 1 === 0 ? 0 : 2 }).format(amount);
}

function formatCurrencyRounded(cents: number, currency = 'usd') {
  const amount = Number.isFinite(cents) ? Math.round(cents / 100) : 0;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDateTimeLabel(unixSeconds: number | null | undefined) {
  if (!unixSeconds || !Number.isFinite(unixSeconds) || unixSeconds <= 0) return null;
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(unixSeconds * 1000));
}

function formatHoursBand(minMinutes: number | null | undefined, maxMinutes: number | null | undefined) {
  const minHours = Number.isFinite(Number(minMinutes)) && Number(minMinutes) > 0 ? Number(minMinutes) / 60 : null;
  const maxHours = Number.isFinite(Number(maxMinutes)) && Number(maxMinutes) > 0 ? Number(maxMinutes) / 60 : null;
  const normalize = (hours: number) => { const rounded = Math.round(hours * 10) / 10; return rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1); };
  if (minHours !== null && maxHours !== null) return minHours === maxHours ? `${normalize(minHours)}h / mo` : `${normalize(minHours)}-${normalize(maxHours)}h / mo`;
  if (minHours !== null) return `${normalize(minHours)}h+ / mo`;
  if (maxHours !== null) return `Up to ${normalize(maxHours)}h / mo`;
  return '--';
}

function formatHoursRange(minHours: number | null, maxHours: number | null) {
  const normalize = (hours: number) => {
    const rounded = Math.round(hours * 10) / 10;
    return rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1);
  };

  if (minHours !== null && maxHours !== null) {
    return minHours === maxHours
      ? `${normalize(minHours)} service hours/mo`
      : `${normalize(minHours)}-${normalize(maxHours)} service hours/mo`;
  }
  if (minHours !== null) return `${normalize(minHours)}+ service hours/mo`;
  if (maxHours !== null) return `Up to ${normalize(maxHours)} service hours/mo`;
  return null;
}

function getTierCapacityLabel(tier: ServiceCatalogTier): string | null {
  const minMinutes = Number((tier.metadata as Record<string, unknown>)?.contractedMonthlyMinutesMin);
  const maxMinutes = Number((tier.metadata as Record<string, unknown>)?.contractedMonthlyMinutesMax);
  if ((Number.isFinite(minMinutes) && minMinutes > 0) || (Number.isFinite(maxMinutes) && maxMinutes > 0)) {
    return formatHoursBand(
      Number.isFinite(minMinutes) ? minMinutes : null,
      Number.isFinite(maxMinutes) ? maxMinutes : null,
    ).replace('/ mo', ' service hours/mo');
  }

  const deliveryHours = (tier.metadata as Record<string, unknown>)?.deliveryHours;
  if (deliveryHours && typeof deliveryHours === 'object') {
    const min = Number((deliveryHours as Record<string, unknown>).min);
    const max = Number((deliveryHours as Record<string, unknown>).max);
    return formatHoursRange(
      Number.isFinite(min) && min > 0 ? min : null,
      Number.isFinite(max) && max > 0 ? max : null,
    );
  }

  return null;
}

function getTierClientSuccessMeetingsLabel(tier: ServiceCatalogTier): string | null {
  const metadata =
    tier.metadata && typeof tier.metadata === 'object' && !Array.isArray(tier.metadata)
      ? (tier.metadata as Record<string, unknown>)
      : {};

  const meetingsRaw = metadata.clientSuccessMeetingsPerMonth;
  const meetingsPerMonth =
    typeof meetingsRaw === 'number'
      ? meetingsRaw
      : typeof meetingsRaw === 'string' && meetingsRaw.trim()
        ? Number.parseInt(meetingsRaw, 10)
        : null;

  if (typeof meetingsPerMonth === 'number' && Number.isFinite(meetingsPerMonth)) {
    if (meetingsPerMonth <= 0) return 'No client success meetings/mo';
    if (meetingsPerMonth === 1) return '1 client success meeting/mo';
    return `${meetingsPerMonth} client success meetings/mo`;
  }

  const cadenceType =
    typeof metadata.clientSuccessCadenceType === 'string'
      ? metadata.clientSuccessCadenceType.trim().toLowerCase()
      : '';

  if (cadenceType === 'weekly') return '4 client success meetings/mo';
  if (cadenceType === 'biweekly') return '2 client success meetings/mo';
  if (cadenceType === 'monthly') return '1 client success meeting/mo';
  if (cadenceType === 'quarterly') return '1 client success meeting/quarter';

  return null;
}

function normalizeServiceCategory(value: string | null | undefined): ServiceCategory | null {
  const normalized = normalizeCode(value);
  if (!normalized) return null;
  if (normalized.includes('marketing') || normalized === 'mm_basic' || normalized === 'mm_silver' || normalized === 'mm_gold' || normalized === 'mm_platinum') return 'marketing';
  if (normalized.includes('platform') || normalized.includes('digital') || normalized.includes('development') || normalized === 'pl_basic' || normalized === 'pl_silver' || normalized === 'pl_gold' || normalized === 'pl_platinum') return 'platform';
  if (normalized.includes('assets') || normalized.includes('design') || normalized.includes('cybard club') || normalized === 'cybard_club') return 'assets_design';
  if (normalized.includes('custom')) return 'custom';
  return null;
}

function getServiceKey(service: ServiceCatalogService) { return normalizeCode(service.code || service.category || service.title); }
function resolveSelectionSeedServiceKey(
  services: ServiceCatalogService[],
  selectionSeed: ClientServiceExplorerSelectionSeed | null | undefined,
) {
  const normalizedCategory = normalizeServiceCategory(
    selectionSeed?.serviceCategory || selectionSeed?.serviceCode || null,
  );
  const normalizedCode = normalizeCode(
    selectionSeed?.serviceCode || selectionSeed?.serviceCategory || '',
  );
  const match =
    services.find((service) => {
      const serviceCategory = normalizeServiceCategory(
        service.category || service.code || service.title,
      );
      return (
        (normalizedCategory && serviceCategory === normalizedCategory) ||
        getServiceKey(service) === normalizedCode ||
        normalizeCode(service.category) === normalizedCode ||
        normalizeCode(service.code) === normalizedCode
      );
    }) ?? null;
  return match ? getServiceKey(match) : null;
}
function getOfferingKey(offering: ServiceCatalogOffering, index: number) { return cleanText(offering.id) || cleanText(offering.code) || cleanText(offering.name) || `offering-${index}`; }
function getStartingPrice(service: ServiceCatalogService) {
  const cents = service.tiers.map((tier) => Number(tier.amountCents || 0)).filter((value) => Number.isFinite(value) && value >= 0);
  return cents.length ? Math.min(...cents) : 0;
}
function getRequiredOfferingCount(tier: ServiceCatalogTier | null): number {
  if (!tier || !tier.offerings.length) return 0;
  const raw = tier.metadata?.activeLanesIncluded;
  if (typeof raw === 'number' && Number.isFinite(raw)) return Math.max(1, Math.min(tier.offerings.length, Math.round(raw)));
  if (typeof raw === 'string') {
    const normalized = raw.trim().toLowerCase();
    if (normalized === 'all') return tier.offerings.length;
    const parsed = Number.parseInt(normalized, 10);
    if (Number.isFinite(parsed)) return Math.max(1, Math.min(tier.offerings.length, parsed));
  }
  return 1;
}

function formatOfferingCodeLabel(code: string) {
  return code
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase())
    .join(' ');
}

function getTierIncludedOfferingCodes(tier: ServiceCatalogTier | null | undefined) {
  const raw = Array.isArray(tier?.metadata?.includedServiceOfferingCodes)
    ? tier?.metadata?.includedServiceOfferingCodes
    : [];
  return Array.from(
    new Set(
      raw
        .map((value) => cleanText(String(value ?? '')))
        .filter(Boolean),
    ),
  );
}

function mergeTierOfferingsForDisplay(
  service: ServiceCatalogService | null | undefined,
  tier: ServiceCatalogTier | null | undefined,
): ServiceCatalogOffering[] {
  if (!service || !tier) return [];

  const includedCodes = getTierIncludedOfferingCodes(tier);
  const persistedOfferings = Array.isArray(tier.offerings) ? tier.offerings : [];
  const catalog = Array.isArray(service.metadata?.offeringsCatalog)
    ? service.metadata.offeringsCatalog
    : [];

  if (!includedCodes.length) return persistedOfferings;

  const catalogByCode = new Map<string, ServiceCatalogOffering>();
  catalog.forEach((entry, index) => {
    const code = cleanText(String(entry?.code ?? ''));
    if (!code) return;
    catalogByCode.set(code, {
      id: null,
      code,
      name: cleanText(String(entry?.name ?? '')) || formatOfferingCodeLabel(code),
      description: cleanText(String(entry?.description ?? '')) || null,
      sortOrder: Number.isFinite(Number(entry?.sortOrder))
        ? Math.round(Number(entry?.sortOrder))
        : index,
      status: cleanText(String(entry?.status ?? '')) || 'published',
      metadata:
        entry?.metadata && typeof entry.metadata === 'object' && !Array.isArray(entry.metadata)
          ? (entry.metadata as Record<string, unknown>)
          : {},
    });
  });

  const mergedByCode = new Map<string, ServiceCatalogOffering>();

  includedCodes.forEach((code, index) => {
    const fromCatalog = catalogByCode.get(code);
    mergedByCode.set(
      code,
      fromCatalog ?? {
        id: null,
        code,
        name: formatOfferingCodeLabel(code),
        description: null,
        sortOrder: index,
        status: 'published',
        metadata: {},
      },
    );
  });

  persistedOfferings.forEach((offering, index) => {
    const code = cleanText(offering.code || '');
    if (!code) return;
    const existing = mergedByCode.get(code);
    mergedByCode.set(code, {
      ...(existing ?? offering),
      ...offering,
      sortOrder:
        Number.isFinite(Number(offering.sortOrder))
          ? Math.round(Number(offering.sortOrder))
          : existing?.sortOrder ?? index,
      metadata: {
        ...(existing?.metadata ?? {}),
        ...((offering.metadata as Record<string, unknown> | undefined) ?? {}),
      },
    });
  });

  return [...mergedByCode.values()].sort(
    (a, b) =>
      Number(a.sortOrder ?? 0) - Number(b.sortOrder ?? 0) ||
      cleanText(a.name).localeCompare(cleanText(b.name)),
  );
}

function matchOfferingIds(tier: ServiceCatalogTier | null | undefined, labels: string[]) {
  if (!tier) return [] as string[];
  const wanted = new Set(labels.map((value) => normalizeCode(value)).filter(Boolean));
  return tier.offerings.map((offering, index) => ({ key: getOfferingKey(offering, index), label: normalizeCode(offering.name || offering.code || offering.id) }))
    .filter((entry) => entry.label && wanted.has(entry.label))
    .map((entry) => entry.key);
}

function matchOfferingIdsByCodes(
  tier: ServiceCatalogTier | null | undefined,
  codes: string[],
) {
  if (!tier) return [] as string[];
  const wanted = new Set(codes.map((value) => cleanText(value)).filter(Boolean));
  return tier.offerings
    .map((offering, index) => ({
      key:
        cleanText(offering.code || offering.id || offering.name) ||
        getOfferingKey(offering, index),
      code: cleanText(offering.code || offering.id || offering.name),
    }))
    .filter((entry) => entry.code && wanted.has(entry.code))
    .map((entry) => entry.key);
}

function getCurrentServiceOfferingCodes(
  currentService: ServiceWorkspaceCurrentService | null | undefined,
) {
  const directCodes = Array.isArray(currentService?.selectedOfferingCodes)
    ? currentService.selectedOfferingCodes
    : [];
  if (directCodes.length) {
    return Array.from(new Set(directCodes.map((value) => cleanText(value)).filter(Boolean)));
  }
  const snapshotOfferings = Array.isArray(currentService?.displaySnapshot?.offerings)
    ? currentService.displaySnapshot.offerings
    : [];
  return Array.from(
    new Set(
      snapshotOfferings
        .map((offering) => cleanText(offering?.code ?? ''))
        .filter(Boolean),
    ),
  );
}

function getOfferingSelectionValue(
  offering: ServiceCatalogOffering,
  index: number,
  preferCode: boolean,
) {
  if (preferCode) {
    return (
      cleanText(offering.code || offering.id || offering.name) ||
      getOfferingKey(offering, index)
    );
  }
  return getOfferingKey(offering, index);
}
function getTheme(category: ServiceCategory | null, serviceCode?: string | null): Theme {
  const normalizedCategory = category ?? normalizeServiceCategory(serviceCode);
  if (normalizedCategory === 'platform') return { gradient: 'linear-gradient(135deg, #7d69ff 0%, #4894ff 52%, #16d4c1 100%)', footer: 'linear-gradient(180deg, rgba(0,0,0,.02), rgba(0,0,0,.2))', glow: '0 26px 72px rgba(53, 123, 255, 0.28)' };
  if (normalizedCategory === 'assets_design') return { gradient: 'linear-gradient(135deg, #2ed39c 0%, #17a97d 48%, #1493cf 100%)', footer: 'linear-gradient(180deg, rgba(0,0,0,.02), rgba(0,0,0,.18))', glow: '0 26px 72px rgba(30, 170, 131, 0.28)' };
  return { gradient: 'linear-gradient(135deg, #ff7a3d 0%, #ff446d 52%, #ff1789 100%)', footer: 'linear-gradient(180deg, rgba(0,0,0,.02), rgba(0,0,0,.22))', glow: '0 26px 72px rgba(255, 68, 109, 0.28)' };
}

function getServiceOverviewCopy(service: ServiceCatalogService | null): string {
  const category = service
    ? normalizeServiceCategory(service.category || service.code || service.title)
    : null;

  if (category === 'marketing') {
    return 'Managed Marketing is a recurring growth partnership where CyBard operates as your external marketing team. It is built for organizations that need ongoing strategy, channel execution, reporting, and campaign momentum without hiring a full in-house department.';
  }

  if (category === 'platform') {
    return 'Platform Development is a recurring product and engineering engagement for businesses that need consistent delivery across websites, platforms, integrations, and digital infrastructure. It gives your account dedicated execution capacity to maintain, improve, and expand the systems your business depends on.';
  }

  if (category === 'assets_design') {
    return 'Assets/Design is CyBard Club, an async design subscription built for fast-moving creative production. It gives your team an unlimited request queue with one active delivery lane at a time, making it ideal for ongoing brand assets, marketing collateral, and design support without project-by-project scoping.';
  }

  return cleanText(service?.description) || 'Explore this service offering and review how it fits into your account.';
}

function buildCheckoutPayload(
  selectedService: ServiceCatalogService,
  selectedTier: ServiceCatalogTier,
  selectedOfferingIds: string[],
  selectedOfferingLabels: string[],
  discountCode: string,
  options?: {
    currentServiceId?: string | null;
    requestedAction?: 'purchase' | 'update' | 'upgrade';
    includeOnboarding?: boolean;
  },
): ServiceConfigSubmission {
  const tierSkuId = cleanText(selectedTier.skuId);
  const requiredOnboardingSkuId =
    options?.includeOnboarding === false
      ? ''
      : cleanText(selectedTier.requiredOnboardingSkuId);
  return {
    serviceCategory: cleanText(selectedService.category),
    serviceCode: cleanText(selectedService.code || selectedService.category),
    serviceName: cleanText(selectedService.title),
    tierSkuId,
    tierName: cleanText(selectedTier.name),
    currentServiceId: cleanText(options?.currentServiceId),
    requestedAction: options?.requestedAction,
    cartItems: [{ skuId: tierSkuId, quantity: 1 }, ...Array.from(new Set([requiredOnboardingSkuId].filter(Boolean))).map((skuId) => ({ skuId, quantity: 1 }))],
    laneSelections: selectedOfferingIds.length ? [{ tierSkuId, laneIds: [...selectedOfferingIds], laneLabels: [...selectedOfferingLabels] }] : undefined,
    discountCode: cleanText(discountCode) || undefined,
    configurationSnapshot: {
      serviceCode: cleanText(selectedService.code || selectedService.category),
      tierSkuId,
      selectedOfferingLabels,
      discountCode: cleanText(discountCode) || undefined,
      requestedAction: options?.requestedAction,
      currentServiceId: cleanText(options?.currentServiceId),
      mode: 'client_services_explorer',
    },
  };
}

function buildExplorerSkuMap(services: ServiceCatalogService[]) {
  const map = new Map<string, ExplorerSku>();
  for (const service of services) {
    const category =
      normalizeServiceCategory(service.category || service.code || service.title) ??
      'custom';
    for (const tier of service.tiers) {
      const skuId = cleanText(tier.skuId);
      if (!skuId || map.has(skuId)) continue;
      map.set(skuId, {
        skuId,
        name: cleanText(tier.name) || skuId,
        billingType:
          cleanText(tier.billingType) === 'one_time' ? 'one_time' : 'recurring',
        interval:
          cleanText(tier.billingInterval) === 'month' ? 'month' : undefined,
        amountCents: Number(tier.amountCents || 0) || 0,
        serviceCategory: category,
        isPrimaryService: Boolean(tier.isPrimaryService),
      });
    }
    for (const addOn of service.addOns) {
      const skuId = cleanText(addOn.skuId);
      if (!skuId || map.has(skuId)) continue;
      map.set(skuId, {
        skuId,
        name: cleanText(addOn.name) || skuId,
        billingType:
          cleanText(addOn.billingType) === 'one_time' ? 'one_time' : 'recurring',
        interval:
          cleanText(addOn.billingType) === 'one_time' ? undefined : 'month',
        amountCents: Number(addOn.amountCents || 0) || 0,
        serviceCategory: category,
        isPrimaryService: false,
      });
    }
  }
  return map;
}

function getPreferredCarouselIndex(
  services: ServiceCatalogService[],
  currentServices: ServiceWorkspaceCurrentService[],
) {
  if (!services.length) return 0;

  const activeCategories = new Set<ServiceCategory>();
  currentServices.forEach((service) => {
    const category =
      normalizeServiceCategory(service.serviceCode) ||
      normalizeServiceCategory(service.serviceName);
    if (category) activeCategories.add(category);
  });

  const preferredIndex = services.findIndex((service) => {
    const category = normalizeServiceCategory(
      service.category || service.code || service.title,
    );
    return Boolean(category && activeCategories.has(category));
  });

  return preferredIndex >= 0
    ? preferredIndex
    : Math.min(1, Math.max(0, services.length - 1));
}

function calcOrderFromExplorerCatalog(
  cartItems: readonly CartItemInput[],
  existingPrimaryCategories: readonly ServiceCategory[],
  skuMap: Map<string, ExplorerSku>,
): CalculatedOrder | null {
  const merged = new Map<string, number>();
  for (const item of cartItems) {
    const skuId = cleanText(item.skuId);
    const quantity = Number(item.quantity);
    if (!skuId || !Number.isInteger(quantity) || quantity <= 0) continue;
    merged.set(skuId, (merged.get(skuId) ?? 0) + quantity);
  }

  const lineItems: CalculatedOrder['lineItems'] extends readonly (infer T)[] ? T[] : never = [];
  let subtotalRecurringCents = 0;
  let subtotalOneTimeCents = 0;
  let recurringPrimarySubtotalCents = 0;
  const recurringPrimaryCategories = new Set<ServiceCategory>();
  const existing = new Set<ServiceCategory>(
    existingPrimaryCategories.filter((value) =>
      PRIMARY_SERVICE_CATEGORIES.includes(value as (typeof PRIMARY_SERVICE_CATEGORIES)[number]),
    ),
  );

  for (const [skuId, quantity] of merged.entries()) {
    const sku = getSkuById(skuId) ?? skuMap.get(skuId);
    if (!sku) return null;
    const lineSubtotalCents = sku.amountCents * quantity;
    lineItems.push({
      skuId: sku.skuId,
      name: sku.name,
      billingType: sku.billingType,
      interval: sku.interval,
      unitAmountCents: sku.amountCents,
      quantity,
      lineSubtotalCents,
      isPrimaryService: sku.isPrimaryService,
      serviceCategory: sku.serviceCategory,
    });
    if (sku.billingType === 'recurring') {
      subtotalRecurringCents += lineSubtotalCents;
      const discountEligible =
        sku.isPrimaryService &&
        PRIMARY_SERVICE_CATEGORIES.includes(
          sku.serviceCategory as (typeof PRIMARY_SERVICE_CATEGORIES)[number],
        );
      if (discountEligible) {
        recurringPrimarySubtotalCents += lineSubtotalCents;
        recurringPrimaryCategories.add(sku.serviceCategory);
      }
    } else {
      subtotalOneTimeCents += lineSubtotalCents;
    }
  }

  lineItems.sort((a, b) => a.skuId.localeCompare(b.skuId));

  const resultingPrimaryCategories = new Set<ServiceCategory>(existing);
  recurringPrimaryCategories.forEach((value) => resultingPrimaryCategories.add(value));
  const hasBundleDiscount =
    recurringPrimarySubtotalCents > 0 && resultingPrimaryCategories.size >= 2;
  const discountCents = hasBundleDiscount
    ? roundPercentCents(recurringPrimarySubtotalCents, 15)
    : 0;
  const recurringMonthlyCents = Math.max(0, subtotalRecurringCents - discountCents);
  const totalDueNowCents = recurringMonthlyCents + subtotalOneTimeCents;

  return {
    lineItems,
    subtotalRecurringCents,
    subtotalOneTimeCents,
    discountCents,
    discountReason: hasBundleDiscount
      ? '15% bundle discount on recurring primary services'
      : null,
    discountBreakdown: hasBundleDiscount
      ? [
          {
            code: 'bundle_primary_services',
            label: '15% bundle discount on recurring primary services',
            amountCents: discountCents,
          },
        ]
      : [],
    totalDueNowCents,
    recurringMonthlyCents,
  };
}

function calcOrderSafely(
  cartItems: readonly CartItemInput[],
  options: { promoCode?: string | null; existingPrimaryCategories: readonly ServiceCategory[] },
  skuMap: Map<string, ExplorerSku>,
): CalculatedOrder | null {
  try {
    return calcOrder(cartItems, options);
  } catch (error) {
    const fallback = calcOrderFromExplorerCatalog(
      cartItems,
      options.existingPrimaryCategories,
      skuMap,
    );
    if (fallback) {
      console.warn('Falling back to explorer catalog pricing for order preview.', error);
      return fallback;
    }
    console.error('Unable to calculate explorer order preview.', error);
    return null;
  }
}

function ServiceCard({ service, currentService, currentTier, selectedTier, selectedOfferingLabels, availableOfferingCount = 0, price, mode, theme, layoutId, onClick }:{
  service: ServiceCatalogService; currentService: ServiceWorkspaceCurrentService | null; currentTier?: ServiceCatalogTier | null; selectedTier: ServiceCatalogTier | null; selectedOfferingLabels: string[]; availableOfferingCount?: number; price: CalculatedOrder | null; mode: 'deck' | 'detail'; theme: Theme; layoutId: string; onClick?: () => void;
}) {
  const title = cleanText(service.title) || cleanText(service.code) || 'Service';
  const isCurrent = Boolean(currentService);
  const category =
    normalizeServiceCategory(service.category || service.code || service.title) ?? null;
  const isClubService = category === 'assets_design';
  const priceLabel = mode === 'detail' ? price?.recurringMonthlyCents || Number(selectedTier?.amountCents || 0) : getStartingPrice(service);
  const showCurrentPlanLabel = mode === 'deck' && isCurrent;
  const selectedTierAmount = Number(selectedTier?.amountCents || 0) || 0;
  const currentTierAmount = Number(currentTier?.amountCents || 0) || 0;
  const sameCurrentTier =
    Boolean(currentService) &&
    cleanText(selectedTier?.skuId) === cleanText(currentService?.tierSkuId);
  const samePriceAsCurrent =
    Boolean(currentService) &&
    selectedTierAmount > 0 &&
    currentTierAmount > 0 &&
    selectedTierAmount === currentTierAmount;
  const showCurrentPlanDetail =
    mode === 'detail' && Boolean(currentService) && (sameCurrentTier || samePriceAsCurrent);
  const showUpgradeDelta =
    mode === 'detail' &&
    Boolean(currentService) &&
    selectedTierAmount > currentTierAmount &&
    currentTierAmount > 0;
  const includesAllOfferings =
    !isClubService &&
    availableOfferingCount > 0 &&
    selectedOfferingLabels.length > 0 &&
    selectedOfferingLabels.length >= availableOfferingCount;
  const deckTierCapacityLabel =
    mode === 'deck' && isCurrent && currentTier ? getTierCapacityLabel(currentTier) : null;
  const deckTierMeetingsLabel =
    mode === 'deck' && isCurrent && currentTier ? getTierClientSuccessMeetingsLabel(currentTier) : null;
  const deckOfferingLabels = selectedOfferingLabels
    .map((value) => cleanText(value))
    .filter(Boolean);
  const detailFooterPrimary = showCurrentPlanDetail
    ? 'Current plan'
    : showUpgradeDelta
      ? `${formatCurrencyRounded(selectedTierAmount - currentTierAmount, selectedTier?.currency || 'usd')} to upgrade`
      : formatCurrency(priceLabel);
  const detailOfferingSummary = isClubService
    ? 'Unlimited async design requests with one active delivery lane.'
    : includesAllOfferings
      ? 'All offerings included'
      : (selectedOfferingLabels.length ? selectedOfferingLabels.join(', ') : 'Selections pending');
  return (
    <CardButton type="button" layoutId={layoutId} $mode={mode} $theme={theme} onClick={onClick} whileTap={{ scale: 0.985 }}>
      <div style={{ position: 'absolute', inset: 1, borderRadius: 28, background: 'linear-gradient(180deg, rgba(255,255,255,.22), rgba(255,255,255,0) 58%)', pointerEvents: 'none' }} />
      <div style={{ position: 'relative', minHeight: '100%', height: '100%', display: 'flex', flexDirection: 'column', flex: 1 }}>
        <AnimatePresence initial={false} mode="wait">
          {mode === 'deck' ? (
            <CardBody key="marketing" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.18 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                <CardEyebrow>{title.toUpperCase()} service family</CardEyebrow>
                {isCurrent ? <CardStatus $current>Current service</CardStatus> : null}
              </div>
              <DeckServiceTitle>{title}</DeckServiceTitle>
              <CardCopy>{cleanText(service.description) || cleanText(service.subtitle) || cleanText(service.teaser) || 'Explore this service family.'}</CardCopy>
              {isCurrent ? (
                <ActivePlanDetails>
                  {includesAllOfferings ? (
                    <ActivePlanText>All offerings included</ActivePlanText>
                  ) : deckOfferingLabels.length ? (
                    <ActivePlanList>
                      {deckOfferingLabels.map((label) => <li key={label}>{label}</li>)}
                    </ActivePlanList>
                  ) : null}
                  {deckTierCapacityLabel ? <ActivePlanHours>{deckTierCapacityLabel}</ActivePlanHours> : null}
                  {deckTierMeetingsLabel ? <ActivePlanHours>{deckTierMeetingsLabel}</ActivePlanHours> : null}
                </ActivePlanDetails>
              ) : null}
            </CardBody>
          ) : (
            <CardBody key="detail" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.18 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                <CardEyebrow>Service snapshot</CardEyebrow>
                {isCurrent ? <CardStatus $current>Current service</CardStatus> : null}
              </div>
              <DetailServiceTitle>{title}</DetailServiceTitle>
              <SnapshotStack>
                <SnapshotItem>
                  <SnapshotLabel>Service</SnapshotLabel>
                  <SnapshotValue>{title}</SnapshotValue>
                </SnapshotItem>
                <SnapshotItem>
                  <SnapshotLabel>{isClubService ? 'Subscription' : 'Tier'}</SnapshotLabel>
                  <SnapshotValue>{cleanText(selectedTier?.name) || cleanText(currentService?.tierName) || (isClubService ? 'CyBard Club' : 'Select tier')}</SnapshotValue>
                </SnapshotItem>
                <SnapshotItem>
                  <SnapshotLabel>{isClubService ? 'Delivery model' : 'Selected offerings'}</SnapshotLabel>
                  <SnapshotValue>{detailOfferingSummary}</SnapshotValue>
                </SnapshotItem>
              </SnapshotStack>
            </CardBody>
          )}
        </AnimatePresence>
        <CardFooter $theme={theme}>
          {showCurrentPlanLabel ? (
            <FooterPrimaryValue>
              Current plan
            </FooterPrimaryValue>
          ) : (
            <FooterPriceRow>
              <FooterPrimaryValue>
                {mode === 'detail' ? detailFooterPrimary : formatCurrency(priceLabel)}
              </FooterPrimaryValue>
              {mode === 'detail' ? (
                !showCurrentPlanDetail && !showUpgradeDelta ? (
                  <FooterPrimaryMeta>projected monthly</FooterPrimaryMeta>
                ) : null
              ) : (
                <FooterPrimaryMeta>starting</FooterPrimaryMeta>
              )}
            </FooterPriceRow>
          )}
          <FooterSupportText>
            {mode === 'detail'
              ? 'Live snapshot of the configuration being built before checkout.'
              : (isCurrent
                  ? 'Current service line attached to this account.'
                  : `Select ${title} to continue building the service configuration.`)}
          </FooterSupportText>
        </CardFooter>
      </div>
    </CardButton>
  );
}

export default function ClientServiceExplorer({
  orgId,
  services,
  currentServices,
  canManageServices,
  actionMode = 'checkout',
  checkoutBusy = false,
  selectionSeed = null,
  onCheckout,
}: Props) {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const paymentMethods = useSelector((state: RootState) => state.billing.paymentMethods);
  const reduceMotion = useReducedMotion();
  const [isMobileSnapshotMode, setIsMobileSnapshotMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 760px)').matches;
  });
  const [mobileSnapshotOpen, setMobileSnapshotOpen] = useState(false);
  const [mobileOfferingDrawer, setMobileOfferingDrawer] =
    useState<MobileOfferingDrawerState | null>(null);
  const isProposalMode = actionMode === 'proposal';
  const [carouselIndex, setCarouselIndex] = useState(() =>
    getPreferredCarouselIndex(services, currentServices),
  );
  const [selectedServiceKey, setSelectedServiceKey] = useState<string | null>(null);
  const [selectedTierSkuId, setSelectedTierSkuId] = useState('');
  const [selectedOfferingIds, setSelectedOfferingIds] = useState<string[]>([]);
  const [discountCode, setDiscountCode] = useState('');
  const [discountStepComplete, setDiscountStepComplete] = useState(false);
  const [focusedStep, setFocusedStep] = useState<StepKey>('tier');
  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false);
  const [purchaseModalPhase, setPurchaseModalPhase] = useState<
    'review' | 'processing' | 'success'
  >('review');
  const [purchaseModalError, setPurchaseModalError] = useState<string | null>(null);
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false);
  const [paymentMethodsError, setPaymentMethodsError] = useState<string | null>(null);
  const [upgradePreviewLoading, setUpgradePreviewLoading] = useState(false);
  const [upgradePreviewError, setUpgradePreviewError] = useState<string | null>(null);
  const [upgradePreview, setUpgradePreview] = useState<CurrentServicePreviewResponse | null>(null);
  const explorerSkuMap = useMemo(() => buildExplorerSkuMap(services), [services]);

  const activeServicesByCategory = useMemo(() => {
    const map = new Map<ServiceCategory, ServiceWorkspaceCurrentService>();
    currentServices.forEach((service) => {
      const category = normalizeServiceCategory(service.serviceCode) || normalizeServiceCategory(service.serviceName);
      if (category && !map.has(category)) map.set(category, service);
    });
    return map;
  }, [currentServices]);
  const selectionSeedKey = cleanText(selectionSeed?.key);
  const selectionSeedServiceKey = useMemo(
    () => resolveSelectionSeedServiceKey(services, selectionSeed),
    [selectionSeed, services],
  );

  useEffect(() => {
    if (selectedServiceKey) return;
    setCarouselIndex(getPreferredCarouselIndex(services, currentServices));
  }, [currentServices, selectedServiceKey, services]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const media = window.matchMedia('(max-width: 760px)');
    const sync = () => setIsMobileSnapshotMode(media.matches);
    sync();
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    if (selectedServiceKey && isMobileSnapshotMode) return;
    setMobileSnapshotOpen(false);
    setMobileOfferingDrawer(null);
  }, [isMobileSnapshotMode, selectedServiceKey]);

  useEffect(() => {
    setMobileOfferingDrawer(null);
  }, [selectedTierSkuId]);

  useEffect(() => {
    if (
      !isMobileSnapshotMode ||
      (!mobileSnapshotOpen && !mobileOfferingDrawer)
    ) {
      return undefined;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileSnapshotMode, mobileOfferingDrawer, mobileSnapshotOpen]);

  useEffect(() => {
    if (!selectionSeedKey || !selectionSeedServiceKey) return;
    setSelectedServiceKey(selectionSeedServiceKey);
    const nextIndex = services.findIndex(
      (service) => getServiceKey(service) === selectionSeedServiceKey,
    );
    if (nextIndex >= 0) setCarouselIndex(nextIndex);
  }, [selectionSeedKey, selectionSeedServiceKey, services]);

  const selectedService = services.find((service) => getServiceKey(service) === selectedServiceKey) ?? null;
  const selectedCategory = selectedService ? normalizeServiceCategory(selectedService.category || selectedService.code || selectedService.title) : null;
  const seedAppliesToSelectedService =
    Boolean(selectionSeedKey) &&
    Boolean(selectionSeedServiceKey) &&
    Boolean(selectedServiceKey) &&
    selectionSeedServiceKey === selectedServiceKey;
  const seedOfferingCodes = useMemo(
    () =>
      seedAppliesToSelectedService
        ? Array.from(
            new Set(
              (selectionSeed?.offeringCodes ?? [])
                .map((value) => cleanText(value))
                .filter(Boolean),
            ),
          )
        : [],
    [seedAppliesToSelectedService, selectionSeed?.offeringCodes],
  );
  const seedOfferingLabels = useMemo(
    () =>
      seedAppliesToSelectedService
        ? Array.from(
            new Set(
              (selectionSeed?.offeringLabels ?? [])
                .map((value) => cleanText(value))
                .filter(Boolean),
            ),
          )
        : [],
    [seedAppliesToSelectedService, selectionSeed?.offeringLabels],
  );
  const currentService = selectedCategory ? activeServicesByCategory.get(selectedCategory) ?? null : null;
  const theme = getTheme(selectedCategory, selectedService?.code);
  const serviceOverviewCopy = useMemo(
    () => getServiceOverviewCopy(selectedService),
    [selectedService],
  );
  const showTierStep = Boolean(selectedService && selectedService.tiers.length > 1);
  const isFixedSubscriptionService =
    selectedCategory === 'assets_design' && Boolean(selectedService?.tiers.length === 1);

  useEffect(() => {
    if (!selectedService) return;
    const currentTier = selectedService.tiers.find((tier) => cleanText(tier.skuId) === cleanText(currentService?.tierSkuId)) ?? null;
    const seededTier = seedAppliesToSelectedService
      ? selectedService.tiers.find(
          (tier) =>
            cleanText(tier.skuId) === cleanText(selectionSeed?.tierSkuId),
        ) ?? null
      : null;
    const initialTier =
      seededTier
        ? seededTier
        : currentService
        ? currentTier
        : showTierStep
          ? null
          : currentTier ?? selectedService.tiers[0] ?? null;
    setSelectedTierSkuId(cleanText(initialTier?.skuId));
    setDiscountCode(
      seedAppliesToSelectedService
        ? cleanText(selectionSeed?.discountCode)
        : '',
    );
    setDiscountStepComplete(false);
    setFocusedStep(
      seededTier
        ? initialTier?.offerings.length
          ? 'offerings'
          : 'tier'
        : showTierStep
        ? 'tier'
        : initialTier?.offerings.length
          ? 'offerings'
          : 'tier',
    );
  }, [
    currentService,
    currentService?.tierSkuId,
    seedAppliesToSelectedService,
    selectedService,
    selectionSeed?.discountCode,
    selectionSeed?.tierSkuId,
    showTierStep,
  ]);

  const selectedTier = useMemo(() => {
    const tier =
      selectedService?.tiers.find(
        (entry) => cleanText(entry.skuId) === cleanText(selectedTierSkuId),
      ) ?? null;
    if (!tier || !selectedService) return tier;
    return {
      ...tier,
      offerings: mergeTierOfferingsForDisplay(selectedService, tier),
    };
  }, [selectedService, selectedTierSkuId]);

  const marketingLaneOptions = useMemo(
    () =>
      selectedCategory === 'marketing'
        ? readServiceLaneOptions(selectedService)
        : [],
    [selectedCategory, selectedService],
  );
  const marketingLaneRule = useMemo(
    () =>
      selectedCategory === 'marketing'
        ? selectedTier && marketingLaneOptions.length
          ? getLaneRuleForTier(selectedTier, marketingLaneOptions)
          : null
        : null,
    [marketingLaneOptions, selectedCategory, selectedTier],
  );
  const laneOptionByCode = useMemo(
    () =>
      new Map(
        marketingLaneOptions.map((lane) => [cleanText(lane.id), lane] as const),
      ),
    [marketingLaneOptions],
  );
  const visibleTierOfferings = useMemo(() => {
    if (!selectedTier) return [] as ServiceCatalogOffering[];
    if (!marketingLaneRule) return selectedTier.offerings;
    const allowedSet = new Set(marketingLaneRule.allowedLaneIds);
    return selectedTier.offerings.filter((offering) =>
      allowedSet.has(cleanText(offering.code || offering.id || offering.name)),
    );
  }, [marketingLaneRule, selectedTier]);
  const displayOfferings = useMemo(
    () =>
      visibleTierOfferings.length
        ? visibleTierOfferings
        : selectedTier?.offerings ?? [],
    [selectedTier?.offerings, visibleTierOfferings],
  );
  const marketingOfferingSections = useMemo(() => {
    if (!marketingLaneRule) return [] as Array<{
      type: 'core' | 'advanced' | 'specialty';
      label: string;
      offerings: ServiceCatalogOffering[];
    }>;

    const orderedTypes = ['core', 'advanced', 'specialty'] as const;
    const labelByType = {
      core: 'Core lanes',
      advanced: 'Advanced lanes',
      specialty: 'Specialty lanes',
    } as const;

    return orderedTypes
      .map((type) => ({
        type,
        label: labelByType[type],
        offerings: displayOfferings.filter((offering) => {
          const code = cleanText(offering.code || offering.id || offering.name);
          return (laneOptionByCode.get(code)?.type ?? 'core') === type;
        }),
      }))
      .filter((section) => section.offerings.length > 0);
  }, [displayOfferings, laneOptionByCode, marketingLaneRule]);

  useEffect(() => {
    if (!selectedTier) { setSelectedOfferingIds([]); return; }
    const tierForMatching =
      visibleTierOfferings.length
        ? { ...selectedTier, offerings: visibleTierOfferings }
        : selectedTier;
    const nextMatchesSeedTier =
      seedAppliesToSelectedService &&
      cleanText(selectedTier.skuId) === cleanText(selectionSeed?.tierSkuId);
    const nextIsCurrentTier =
      Boolean(currentService) &&
      cleanText(selectedTier.skuId) === cleanText(currentService?.tierSkuId);
    const currentOfferingCodes = getCurrentServiceOfferingCodes(currentService);
    const seededMatched = nextMatchesSeedTier
      ? (
          seedOfferingCodes.length &&
          matchOfferingIdsByCodes(tierForMatching, seedOfferingCodes).length
            ? matchOfferingIdsByCodes(tierForMatching, seedOfferingCodes)
            : matchOfferingIds(tierForMatching, seedOfferingLabels)
        )
      : [];
    const matched = seededMatched.length
      ? seededMatched
      : currentService && nextIsCurrentTier
        ? (
            matchOfferingIdsByCodes(
              tierForMatching,
              currentOfferingCodes,
            ).length
              ? matchOfferingIdsByCodes(
                  tierForMatching,
                  currentOfferingCodes,
                )
              : matchOfferingIds(
                  tierForMatching,
                  currentService.selectedOfferings,
                )
          )
        : [];
    const required = marketingLaneRule
      ? marketingLaneRule.mode === 'all'
        ? marketingLaneRule.allowedLaneIds.length
        : marketingLaneRule.maxSelections
      : getRequiredOfferingCount(selectedTier);
    const allIds = visibleTierOfferings.length
      ? visibleTierOfferings.map((offering, index) =>
          getOfferingSelectionValue(
            offering,
            index,
            Boolean(marketingLaneRule),
          ),
        )
      : selectedTier.offerings.map((offering, index) =>
          getOfferingSelectionValue(
            offering,
            index,
            Boolean(marketingLaneRule),
          ),
        );
    const nextSelection = matched.length
      ? matched
      : required >= allIds.length
        ? allIds
        : [];
    setSelectedOfferingIds(
      marketingLaneRule
        ? sanitizeLaneSelection(marketingLaneRule, nextSelection)
        : nextSelection,
    );
    setDiscountStepComplete(false);
    setFocusedStep(
      visibleTierOfferings.length || nextIsCurrentTier
        ? 'offerings'
        : selectedTier.offerings.length
          ? 'offerings'
          : 'tier',
    );
  }, [
    currentService,
    currentService?.tierSkuId,
    marketingLaneRule,
    seedAppliesToSelectedService,
    seedOfferingCodes,
    seedOfferingLabels,
    selectedTier,
    selectionSeed?.tierSkuId,
    showTierStep,
    visibleTierOfferings,
  ]);

  const requiredOfferingCount = marketingLaneRule
    ? marketingLaneRule.mode === 'all'
      ? marketingLaneRule.allowedLaneIds.length
      : marketingLaneRule.maxSelections
    : getRequiredOfferingCount(selectedTier);
  const marketingAllMode = marketingLaneRule?.mode === 'all';
  const useCodeBasedOfferingSelection = selectedCategory === 'marketing';
  const offeringsRequired = Boolean(
    marketingLaneRule ? visibleTierOfferings.length : selectedTier?.offerings.length,
  );
  const selectedOfferingLabels = useMemo(() => {
    if (!selectedTier) return [] as string[];
    const selected = new Set(selectedOfferingIds);
    const sourceOfferings = displayOfferings;
    return sourceOfferings.map((offering, index) => ({
      key: getOfferingSelectionValue(
        offering,
        index,
        useCodeBasedOfferingSelection,
      ),
      label: cleanText(offering.name || offering.code || offering.id),
    }))
      .filter((entry) => entry.label && selected.has(entry.key))
      .map((entry) => entry.label);
  }, [
    displayOfferings,
    selectedOfferingIds,
    selectedTier,
    useCodeBasedOfferingSelection,
  ]);
  const existingPrimaryCategories = useMemo(() => currentServices.map((service) => normalizeServiceCategory(service.serviceCode || service.serviceName)).filter((value): value is ServiceCategory => Boolean(value)), [currentServices]);
  const currentTier = useMemo(
    () =>
      selectedService?.tiers.find(
        (tier) => cleanText(tier.skuId) === cleanText(currentService?.tierSkuId),
      ) ?? null,
    [currentService?.tierSkuId, selectedService],
  );
  const sameActiveTierSelected =
    Boolean(currentService) &&
    cleanText(selectedTier?.skuId) === cleanText(currentService?.tierSkuId);
  const calculatedOrder = useMemo(() => {
    if (!selectedService || !selectedTier) return null;
    const requiredOnboardingSkuId = currentService
      ? ''
      : cleanText(selectedTier.requiredOnboardingSkuId);
    return calcOrderSafely([{ skuId: cleanText(selectedTier.skuId), quantity: 1 }, ...Array.from(new Set([requiredOnboardingSkuId].filter(Boolean))).map((skuId) => ({ skuId, quantity: 1 }))], { promoCode: cleanText(discountCode) || undefined, existingPrimaryCategories }, explorerSkuMap);
  }, [currentService, discountCode, existingPrimaryCategories, explorerSkuMap, selectedService, selectedTier]);
  const currentServicePrice = useMemo(() => {
    if (!selectedService || !currentService) return null;
    const matchedTier = selectedService.tiers.find((tier) => cleanText(tier.skuId) === cleanText(currentService.tierSkuId)) ?? null;
    if (!matchedTier) return null;
    return calcOrderSafely([{ skuId: cleanText(matchedTier.skuId), quantity: 1 }], { existingPrimaryCategories }, explorerSkuMap);
  }, [currentService, existingPrimaryCategories, explorerSkuMap, selectedService]);

  const tierComplete = Boolean(selectedTier);
  const offeringsComplete = Boolean(selectedTier) && (
    !offeringsRequired ||
    (marketingLaneRule
      ? marketingAllMode
        ? selectedOfferingIds.length === requiredOfferingCount
        : selectedOfferingIds.length > 0 &&
          selectedOfferingIds.length <= requiredOfferingCount
      : requiredOfferingCount > 0
        ? selectedOfferingIds.length === requiredOfferingCount
        : selectedOfferingIds.length > 0)
  );
  const offeringStepTitle = marketingLaneRule
    ? 'Lane selection'
    : 'Offerings selection';
  const offeringStepCopy = marketingLaneRule
    ? marketingAllMode
      ? 'Core, advanced, and specialty lanes are grouped below. All lanes included in this tier are already active.'
      : `Core, advanced, and specialty lanes are grouped below. Select up to ${requiredOfferingCount} ${
          requiredOfferingCount === 1 ? 'lane' : 'lanes'
        } for this tier.`
    : offeringsRequired
      ? `Choose ${requiredOfferingCount} ${
          requiredOfferingCount === 1 ? 'offering' : 'offerings'
        } for this tier.`
      : 'No offering selections are required for this service tier.';
  const offeringStatusCopy = marketingLaneRule
    ? marketingAllMode
      ? 'All included lanes are active for this tier.'
      : `${selectedOfferingIds.length}/${requiredOfferingCount} lanes selected. This step is complete once your lane mix reflects the service you want.`
    : `${selectedOfferingIds.length}/${requiredOfferingCount} selected.${
        selectedOfferingIds.length >= requiredOfferingCount
          ? ' This step is complete.'
          : ' Select the required offerings to continue.'
      }`;
  const toggleOfferingSelection = (
    selectionValue: string,
    blocked: boolean,
  ) => {
    if (marketingAllMode || blocked) return false;
    setDiscountStepComplete(false);
    let nextFocus: 'offerings' | 'discount' = 'offerings';
    setSelectedOfferingIds((current) => {
      const next = current.includes(selectionValue)
        ? current.filter((value) => value !== selectionValue)
        : [...current, selectionValue];
      const sanitized = marketingLaneRule
        ? sanitizeLaneSelection(marketingLaneRule, next)
        : next;
      const nextComplete = marketingLaneRule
        ? marketingAllMode
          ? sanitized.length === requiredOfferingCount
          : sanitized.length > 0 && sanitized.length <= requiredOfferingCount
        : requiredOfferingCount > 0
          ? sanitized.length === requiredOfferingCount
          : sanitized.length > 0;
      nextFocus = nextComplete && showDiscountStep ? 'discount' : 'offerings';
      return sanitized;
    });
    setFocusedStep(nextFocus);
    return true;
  };
  const openMobileOfferingDrawer = (nextDrawer: MobileOfferingDrawerState) => {
    setMobileSnapshotOpen(false);
    setMobileOfferingDrawer(nextDrawer);
  };
  const renderOfferingSelectionCard = (
    offering: ServiceCatalogOffering,
    index: number,
    eyebrow: string,
    kindLabel: 'service lane' | 'offering',
  ) => {
    const selectionValue = getOfferingSelectionValue(
      offering,
      index,
      Boolean(marketingLaneRule),
    );
    const active = selectedOfferingIds.includes(selectionValue);
    const blocked = marketingLaneRule
      ? !active &&
        !canSelectLane(marketingLaneRule, selectedOfferingIds, selectionValue)
      : requiredOfferingCount > 0 &&
        selectedOfferingIds.length >= requiredOfferingCount &&
        !active;
    const title =
      cleanText(offering.name || offering.code || offering.id) || 'Offering';
    const description = cleanText(offering.description);

    return (
      <Selection
        key={selectionValue}
        type="button"
        $active={active}
        onClick={(event) => {
          event.stopPropagation();
          if (isMobileSnapshotMode) {
            openMobileOfferingDrawer({
              selectionValue,
              offering,
              eyebrow,
              kindLabel,
            });
            return;
          }
          toggleOfferingSelection(selectionValue, blocked);
        }}
      >
        <SelectionIndicator $active={active}>
          {active ? <Check size={11} /> : null}
        </SelectionIndicator>
        <SelectionBody>
          <OfferingMeta>{eyebrow}</OfferingMeta>
          <SelectionTitle>{title}</SelectionTitle>
          {description ? (
            <SelectionDescription>{description}</SelectionDescription>
          ) : null}
        </SelectionBody>
      </Selection>
    );
  };
  const mobileOfferingDrawerActive = mobileOfferingDrawer
    ? selectedOfferingIds.includes(mobileOfferingDrawer.selectionValue)
    : false;
  const mobileOfferingDrawerBlocked = mobileOfferingDrawer
    ? marketingLaneRule
      ? !mobileOfferingDrawerActive &&
        !canSelectLane(
          marketingLaneRule,
          selectedOfferingIds,
          mobileOfferingDrawer.selectionValue,
        )
      : requiredOfferingCount > 0 &&
        selectedOfferingIds.length >= requiredOfferingCount &&
        !mobileOfferingDrawerActive
    : false;
  const mobileOfferingDrawerStatus = mobileOfferingDrawer
    ? marketingAllMode
      ? `${requiredOfferingCount}/${requiredOfferingCount} ${
          mobileOfferingDrawer.kindLabel === 'service lane'
            ? 'service lanes'
            : 'offerings'
        } active for this tier.`
      : `${selectedOfferingIds.length}/${requiredOfferingCount} ${
          mobileOfferingDrawer.kindLabel === 'service lane'
            ? 'service lanes'
            : 'offerings'
        } selected for this tier.`
    : '';
  const mobileOfferingDrawerCopy = mobileOfferingDrawer
    ? cleanText(mobileOfferingDrawer.offering.description) ||
      (mobileOfferingDrawer.kindLabel === 'service lane'
        ? 'Review this lane and decide whether it should be included in this tier.'
        : 'Review this offering and decide whether it should be included in this tier.')
    : '';
  const mobileOfferingActionLabel = mobileOfferingDrawer
    ? marketingAllMode
      ? 'Included with this tier'
      : mobileOfferingDrawerActive
        ? `Remove ${mobileOfferingDrawer.kindLabel}`
        : `Add ${mobileOfferingDrawer.kindLabel}`
    : '';
  const mobileOfferingActionDisabled = Boolean(
    !mobileOfferingDrawer ||
      marketingAllMode ||
      (mobileOfferingDrawerBlocked && !mobileOfferingDrawerActive),
  );
  const handleMobileOfferingAction = () => {
    if (!mobileOfferingDrawer) return;
    const didToggle = toggleOfferingSelection(
      mobileOfferingDrawer.selectionValue,
      mobileOfferingDrawerBlocked,
    );
    if (didToggle) {
      setMobileOfferingDrawer(null);
    }
  };
  const discountUnlocked = tierComplete && offeringsComplete;
  const primaryPaymentMethod = useMemo(
    () => paymentMethods.find((method) => method.isDefault) ?? paymentMethods[0] ?? null,
    [paymentMethods],
  );
  const checkoutOfferingSummary = useMemo(() => {
    if (isFixedSubscriptionService) {
      return 'Unlimited request queue with one active delivery lane at a time.';
    }
    if (selectedOfferingLabels.length) {
      return selectedOfferingLabels.join(', ');
    }
    if (selectedTier?.offerings.length) {
      return 'Select the required offerings to shape this service.';
    }
    return 'No specific offerings are required for this tier.';
  }, [isFixedSubscriptionService, selectedOfferingLabels, selectedTier]);
  const serviceConfigChanged = useMemo(() => {
    if (!currentService) return true;
    const tierChanged = cleanText(selectedTier?.skuId) !== cleanText(currentService.tierSkuId);
    const normalizeList = (values: string[]) =>
      values
        .map((value) => cleanText(value).toLowerCase())
        .filter(Boolean)
        .sort();
    const currentOfferings = normalizeList(currentService.selectedOfferings);
    const nextOfferings = normalizeList(selectedOfferingLabels);
    const offeringsChanged =
      currentOfferings.length !== nextOfferings.length ||
      currentOfferings.some((value, index) => value !== nextOfferings[index]);
    return tierChanged || offeringsChanged;
  }, [currentService, selectedOfferingLabels, selectedTier]);
  const serviceAction = useMemo<ServiceAction>(() => {
    if (!currentService) return 'purchase';
    if (!serviceConfigChanged) return 'no_change';
    const currentTierAmount = Number(currentTier?.amountCents || 0) || 0;
    const selectedTierAmount = Number(selectedTier?.amountCents || 0) || 0;
    if (selectedTierAmount < currentTierAmount) return 'downgrade_blocked';
    if (
      cleanText(selectedTier?.skuId) === cleanText(currentService.tierSkuId) ||
      selectedTierAmount === currentTierAmount
    ) {
      return 'update';
    }
    return 'upgrade';
  }, [currentService, currentTier?.amountCents, selectedTier?.amountCents, selectedTier?.skuId, serviceConfigChanged]);
  const checkoutPayload = useMemo(() => {
    if (!selectedService || !selectedTier || !discountUnlocked) return null;
    if (serviceAction === 'no_change' || serviceAction === 'downgrade_blocked') {
      return null;
    }
    return buildCheckoutPayload(
      selectedService,
      selectedTier,
      selectedOfferingIds,
      selectedOfferingLabels,
      discountCode,
      {
        currentServiceId: currentService?.id,
        requestedAction: serviceAction,
        includeOnboarding: !currentService,
      },
    );
  }, [
    currentService,
    discountCode,
    discountUnlocked,
    selectedOfferingIds,
    selectedOfferingLabels,
    selectedService,
    selectedTier,
    serviceAction,
  ]);
  const canCheckoutSelection = Boolean(
    discountUnlocked && checkoutPayload && canManageServices,
  );
  const showDiscountStep =
    !isProposalMode && discountUnlocked && !sameActiveTierSelected;
  const railActionLabel =
    isProposalMode
      ? currentService
        ? 'SEND CHANGE PROPOSAL'
        : 'SEND PROPOSAL'
      : serviceAction === 'update'
      ? 'SAVE CHANGES'
      : serviceAction === 'upgrade'
        ? 'UPGRADE SERVICE'
        : 'BUY NOW';
  const railActionMeta =
    isProposalMode
      ? currentService
        ? 'Review this change proposal before sending it to the client portal.'
        : 'Review this service proposal before sending it to the client portal.'
      : serviceAction === 'update'
      ? 'Review the updated configuration and save the changes to this active service.'
      : serviceAction === 'upgrade'
        ? 'Review the saved payment method before confirming this service upgrade.'
        : 'Review the payment method on file before confirming this purchase.';
  const modalTitleText =
    isProposalMode
      ? currentService
        ? 'Review change proposal'
        : 'Review service proposal'
      : serviceAction === 'update'
      ? 'Confirm service update'
      : serviceAction === 'upgrade'
        ? 'Confirm service upgrade'
        : 'Confirm purchase';
  const modalSubText =
    isProposalMode
      ? 'This proposal will be saved for the organization and will appear in the client portal services workspace for review.'
      : serviceAction === 'update'
      ? 'Review the updated configuration before applying it to the active service on this account.'
      : serviceAction === 'upgrade'
        ? 'Review the upgraded configuration and the saved payment method before confirming the tier change.'
        : 'Review the receipt and the payment method already on file before continuing into secure checkout.';
  const modalConfirmText =
    isProposalMode
      ? currentService
        ? 'Send change proposal'
        : 'Send proposal'
      : serviceAction === 'update'
      ? 'Save changes'
      : serviceAction === 'upgrade'
        ? 'Confirm upgrade'
        : 'Confirm purchase';
  const modalRequiresPaymentMethod =
    !isProposalMode &&
    (serviceAction === 'purchase' || serviceAction === 'upgrade');
  const showPaymentPanel = !isProposalMode && modalRequiresPaymentMethod;
  const proposalUpgradeEstimateDeferred =
    isProposalMode &&
    serviceAction === 'upgrade' &&
    !cleanText(currentService?.billingSubscriptionId);
  const modalProjectedMonthlyValue = formatCurrency(
    serviceAction === 'upgrade'
      ? upgradePreview?.recurringMonthlyCents ??
          calculatedOrder?.recurringMonthlyCents ??
          0
      : calculatedOrder?.recurringMonthlyCents ?? 0,
    serviceAction === 'upgrade'
      ? upgradePreview?.currency ?? 'usd'
      : 'usd',
  );
  const modalDiscountValue =
    (serviceAction === 'upgrade'
      ? upgradePreview?.discountCents
      : calculatedOrder?.discountCents)
      ? `${formatCurrency(
          serviceAction === 'upgrade'
            ? upgradePreview?.discountCents ?? 0
            : calculatedOrder?.discountCents ?? 0,
          serviceAction === 'upgrade'
            ? upgradePreview?.currency ?? 'usd'
            : 'usd',
        )} applied`
      : 'No discounts applied yet';
  const modalHeroLabel =
    serviceAction === 'update'
      ? 'Billing impact'
      : isProposalMode && serviceAction === 'upgrade'
        ? 'Estimated due now'
        : isProposalMode
          ? 'Proposed due now'
          : 'Due now';
  const modalHeroValue =
    serviceAction === 'update'
      ? 'No immediate charge'
      : serviceAction === 'upgrade'
        ? proposalUpgradeEstimateDeferred
          ? 'Calculated at review'
          : upgradePreviewLoading
            ? 'Calculating...'
            : upgradePreviewError
              ? 'Unavailable'
              : formatCurrency(
                  upgradePreview?.dueNowCents ?? 0,
                  upgradePreview?.currency ?? 'usd',
                )
        : formatCurrency(calculatedOrder?.totalDueNowCents ?? 0);
  const showTypingHeroValue =
    serviceAction === 'upgrade' &&
    upgradePreviewLoading &&
    !proposalUpgradeEstimateDeferred;
  const modalHeroCopy =
    serviceAction === 'update'
      ? 'This change updates the existing contract and does not create a new immediate charge.'
      : serviceAction === 'upgrade'
        ? proposalUpgradeEstimateDeferred
          ? 'This organization does not have a billing-linked subscription available for a live prorated estimate yet. The current amount will be calculated when the client reviews this proposal.'
          : isProposalMode
            ? upgradePreview && !upgradePreviewLoading && !upgradePreviewError
              ? `This is a live estimate based on the current billing cycle${formatDateTimeLabel(upgradePreview.prorationDate) ? ` as of ${formatDateTimeLabel(upgradePreview.prorationDate)}` : ''}. The client will see the current prorated amount when they review this proposal.`
              : 'The client will see the current prorated amount when they review this proposal.'
            : upgradePreview && !upgradePreviewLoading && !upgradePreviewError
              ? `This upgrade amount is prorated using the remaining time in the current billing cycle${formatDateTimeLabel(upgradePreview.prorationDate) ? ` as of ${formatDateTimeLabel(upgradePreview.prorationDate)}` : ''}.`
              : 'This amount is calculated from the current prorated upgrade charge.'
        : isProposalMode
          ? 'This proposal will be saved for the organization and shown in the client portal for review.'
          : 'This is the amount that will be charged immediately when you confirm checkout.';
  const modalFooterMeta =
    isProposalMode
      ? 'This proposal will be saved to the organization and shown in the client services workspace for review.'
      : modalRequiresPaymentMethod
        ? 'The saved payment method on file will be used when you confirm this action.'
        : 'Confirming will apply this change directly to the active service contract.';
  const processingStateTitle = isProposalMode
    ? 'Sending proposal'
    : serviceAction === 'upgrade'
      ? 'Processing payment'
      : serviceAction === 'update'
        ? 'Applying change'
        : 'Starting checkout';
  const processingStateCopy = isProposalMode
    ? 'The proposal is being saved for this organization now. The modal will close once the workspace refresh confirms it is available to review.'
    : serviceAction === 'upgrade'
      ? 'We are confirming the upgrade payment and waiting for the refreshed service contract to come back from the server. This modal will close automatically as soon as the updated service is loaded.'
      : serviceAction === 'update'
        ? 'We are applying the updated service configuration and waiting for the refreshed contract to return. This modal will close automatically once the change is loaded.'
        : 'We are handing this checkout over to the payment flow now.';
  const successStateTitle = isProposalMode
    ? 'Proposal saved'
    : serviceAction === 'upgrade'
      ? 'Upgrade confirmed'
      : serviceAction === 'update'
        ? 'Service updated'
        : 'Checkout started';
  const successStateCopy = isProposalMode
    ? 'The proposal is now attached to this organization and will appear in the client services workspace.'
    : serviceAction === 'upgrade'
      ? 'The payment was confirmed and the updated service contract is now loading into the workspace.'
      : serviceAction === 'update'
        ? 'The updated contract has been applied and the refreshed service snapshot is loading now.'
        : 'The checkout flow is ready.';
  const railIdleNote =
    serviceAction === 'downgrade_blocked'
      ? 'Downgrades are not available until the current contract term ends.'
      : currentService
        ? 'Select a different tier or offering to continue.'
        : null;

  useEffect(() => {
    if (!selectedService) return;
    if (!tierComplete) { setFocusedStep(showTierStep ? 'tier' : 'discount'); return; }
    if (!offeringsComplete) { setFocusedStep((current) => current === 'discount' ? 'offerings' : current); return; }
      if (!showDiscountStep) {
        setFocusedStep(offeringsRequired ? 'offerings' : 'tier');
        return;
      }
    if (!discountStepComplete) setFocusedStep('discount');
  }, [discountStepComplete, offeringsComplete, offeringsRequired, selectedService, showDiscountStep, showTierStep, tierComplete]);

  useEffect(() => {
    setPurchaseModalOpen(false);
    setPurchaseModalPhase('review');
    setPurchaseModalError(null);
    setPaymentMethodsError(null);
    setUpgradePreview(null);
    setUpgradePreviewError(null);
    setUpgradePreviewLoading(false);
  }, [selectedServiceKey]);

  useEffect(() => {
    if (!purchaseModalOpen) {
      setPurchaseModalPhase('review');
      setPurchaseModalError(null);
    }
  }, [purchaseModalOpen]);

  useEffect(() => {
    setUpgradePreview(null);
    setUpgradePreviewError(null);
    setUpgradePreviewLoading(false);
  }, [selectedTierSkuId, discountCode, selectedOfferingIds]);

  useEffect(() => {
    if (!purchaseModalOpen || !modalRequiresPaymentMethod) return undefined;
    let mounted = true;
    setPaymentMethodsLoading(true);
    setPaymentMethodsError(null);
    void dispatch(fetchPaymentMethods())
      .unwrap()
      .catch((error: unknown) => {
        if (!mounted) return;
        setPaymentMethodsError(
          error instanceof Error
            ? error.message
            : 'Unable to load the payment methods on file for this account.',
        );
      })
      .finally(() => {
        if (mounted) setPaymentMethodsLoading(false);
      });

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && purchaseModalPhase === 'review') {
        setPurchaseModalOpen(false);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => {
      mounted = false;
      window.removeEventListener('keydown', handleEscape);
    };
  }, [dispatch, modalRequiresPaymentMethod, purchaseModalOpen, purchaseModalPhase]);

  useEffect(() => {
    if (
      !purchaseModalOpen ||
      serviceAction !== 'upgrade' ||
      !checkoutPayload ||
      !currentService?.id ||
      !selectedTier
    ) {
      setUpgradePreviewLoading(false);
      if (!purchaseModalOpen || serviceAction !== 'upgrade') {
        setUpgradePreview(null);
        setUpgradePreviewError(null);
      }
      return undefined;
    }

    if (proposalUpgradeEstimateDeferred) {
      setUpgradePreviewLoading(false);
      setUpgradePreview(null);
      setUpgradePreviewError(null);
      return undefined;
    }

    let mounted = true;
    setUpgradePreviewLoading(true);
    setUpgradePreviewError(null);
    setUpgradePreview(null);

    void previewCurrentClientServiceChange({
      orgId,
      currentServiceId: currentService.id,
      tierSkuId: cleanText(selectedTier.skuId),
      laneSelections: checkoutPayload.laneSelections,
      discountCode: cleanText(discountCode) || undefined,
    })
      .then((preview) => {
        if (!mounted) return;
        setUpgradePreview(preview);
      })
      .catch((error: unknown) => {
        if (!mounted) return;
        setUpgradePreviewError(
          error instanceof Error
            ? error.message
            : 'Unable to calculate the upgrade amount for this tier change.',
        );
      })
      .finally(() => {
        if (mounted) setUpgradePreviewLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [
    checkoutPayload,
    currentService?.id,
    discountCode,
    isProposalMode,
    orgId,
    proposalUpgradeEstimateDeferred,
    purchaseModalOpen,
    selectedTier,
    serviceAction,
  ]);

  const handleModalConfirm = async () => {
    if (!checkoutPayload) return;
    setPurchaseModalError(null);
    setPurchaseModalPhase('processing');
    try {
      await Promise.resolve(
        onCheckout({
          ...checkoutPayload,
          prorationDate:
            !isProposalMode && serviceAction === 'upgrade'
              ? upgradePreview?.prorationDate ?? undefined
              : undefined,
          paymentMethodId: modalRequiresPaymentMethod
            ? primaryPaymentMethod?.id ?? undefined
            : undefined,
        }),
      );
      setPurchaseModalPhase('success');
      window.setTimeout(() => {
        setPurchaseModalOpen(false);
      }, 1200);
    } catch (error) {
      setPurchaseModalPhase('review');
      setPurchaseModalError(
        error instanceof Error
          ? error.message
          : 'Unable to complete this service action.',
      );
    }
  };

  return (
    <LayoutGroup id="client-service-explorer">
      <Shell>
        <Viewport>
          <AnimatePresence initial={false} mode="wait">
            {!selectedService ? (
              <DeckView key="deck" initial={reduceMotion ? false : { opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -18 }} transition={{ duration: 0.22, ease: 'easeOut' }}>
                <Carousel3D initialIndex={carouselIndex} onChange={setCarouselIndex}>
                  {services.map((service) => {
                    const category = normalizeServiceCategory(service.category || service.code || service.title);
                    const active = category ? activeServicesByCategory.get(category) ?? null : null;
                    const activeTier =
                      active
                        ? service.tiers.find(
                            (tier) =>
                              cleanText(tier.skuId) === cleanText(active.tierSkuId),
                          ) ?? null
                        : null;
                    const activeOfferingCount = activeTier
                      ? mergeTierOfferingsForDisplay(service, activeTier).length
                      : 0;
                    return <ServiceCard key={getServiceKey(service)} service={service} currentService={active} currentTier={activeTier} selectedTier={null} selectedOfferingLabels={active?.selectedOfferings ?? []} availableOfferingCount={activeOfferingCount} price={null} mode="deck" theme={getTheme(category, service.code)} layoutId={`service-card-${getServiceKey(service)}`} onClick={() => setSelectedServiceKey(getServiceKey(service))} />;
                  })}
                </Carousel3D>
              </DeckView>
            ) : (
              <React.Fragment key={`detail-${selectedServiceKey}`}>
                <DetailView initial={reduceMotion ? false : { opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.22, ease: 'easeOut' }}>
                <Main>
                  <div style={{ display: 'grid', gap: 10 }}>
                    <BackButton type="button" onClick={() => setSelectedServiceKey(null)}><ArrowLeft size={14} />All services</BackButton>
                    <Eyebrow>
                      {isProposalMode
                        ? currentService
                          ? 'Propose a service change'
                          : 'Build a service proposal'
                        : currentService
                          ? 'Adjust this service'
                          : 'Build this service'}
                    </Eyebrow>
                    <Title>{cleanText(selectedService.title) || 'Service'}</Title>
                    <Copy>{serviceOverviewCopy}</Copy>
                    <Copy>{currentService ? `This account currently carries ${cleanText(currentService.tierName) || cleanText(selectedService.title)} under ${cleanText(selectedService.title)}. The current selections are preloaded below so you can review the service the same way you would build a new one.` : isFixedSubscriptionService ? isProposalMode ? 'This is a fixed subscription. There are no tier or offering selections for CyBard Club, so you can move straight into reviewing the proposal.' : 'This is a fixed subscription. There are no tier or offering selections for CyBard Club, so you can move straight into discount review and purchase.' : 'Build this service one decision at a time. Complete the current step and the next step will animate into view.'}</Copy>
                  </div>
                  <div style={{ display: 'grid', gap: 14, maxWidth: 860 }}>
                      <AnimatePresence initial={false}>
                        {showTierStep ? <FlatStep key="tier-step" layout $dimmed={focusedStep !== 'tier' && tierComplete} onClick={() => setFocusedStep('tier')} initial={reduceMotion ? false : { opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.18 }}>
                          <StepText><StepKicker>Step 1</StepKicker><StepTitle>Tier selection</StepTitle><StepCopy>Select the delivery tier that best fits the amount of work and operating cadence you need.</StepCopy></StepText>
                          <TierRow>
                            {selectedService.tiers.map((tier) => {
                              const tierKey = cleanText(tier.skuId) || cleanText(tier.name);
                              const isActive = cleanText(tier.skuId) === cleanText(selectedTierSkuId);
                              const capacityLabel = getTierCapacityLabel(tier);
                              const meetingsLabel = getTierClientSuccessMeetingsLabel(tier);

                              return (
                                <Option
                                  key={tierKey}
                                  type="button"
                                  $active={isActive}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setSelectedTierSkuId(cleanText(tier.skuId));
                                    const nextIsCurrentTier =
                                      Boolean(currentService) &&
                                      cleanText(tier.skuId) === cleanText(currentService?.tierSkuId);
                                    setFocusedStep(
                                      tier.offerings.length
                                        ? 'offerings'
                                        : nextIsCurrentTier
                                          ? 'offerings'
                                          : 'discount',
                                    );
                                  }}
                                >
                                  <div style={{ display: 'grid', gap: 6, alignContent: 'start' }}>
                                    <div style={{ color: '#f3f4f6', fontSize: 14, fontWeight: 900 }}>
                                      {cleanText(tier.name) || 'Tier'}
                                    </div>
                                    <div style={{ color: '#f6c37e', fontSize: 12, fontWeight: 900 }}>
                                      {formatCurrency(Number(tier.amountCents || 0), tier.currency)} / month
                                    </div>
                                  </div>
                                  <div style={{ color: '#c1cad8', fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                                    {cleanText(tier.description) || 'Service tier configuration.'}
                                  </div>
                                  {capacityLabel || meetingsLabel ? (
                                    <OptionFooterStack>
                                      {capacityLabel ? <OptionFooter>{capacityLabel}</OptionFooter> : null}
                                      {meetingsLabel ? <OptionFooter>{meetingsLabel}</OptionFooter> : null}
                                    </OptionFooterStack>
                                  ) : null}
                                </Option>
                              );
                            })}
                          </TierRow>
                        </FlatStep> : null}
                        {tierComplete && offeringsRequired ? (
                          <FlatStep
                            key="offerings-step"
                            layout
                            $dimmed={focusedStep !== 'offerings' && offeringsComplete}
                            onClick={() => setFocusedStep('offerings')}
                            initial={reduceMotion ? false : { opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -12 }}
                            transition={{ duration: 0.18 }}
                          >
                            <StepText>
                              <StepKicker>Step 2</StepKicker>
                              <StepTitle>{offeringStepTitle}</StepTitle>
                              <StepCopy>{offeringStepCopy}</StepCopy>
                            </StepText>
                            {selectedTier ? (
                              <>
                                {marketingOfferingSections.length ? (
                                  marketingOfferingSections.map((section) => (
                                    <OfferingTypeSection key={section.type}>
                                      <OfferingTypeHeading>{section.label}</OfferingTypeHeading>
                                      <SelectionGrid>
                                        {section.offerings.map((offering, index) => {
                                          const offeringCode =
                                            cleanText(
                                              offering.code || offering.id || offering.name,
                                            );
                                          const laneType =
                                            laneOptionByCode.get(offeringCode)?.type ?? 'core';
                                          const laneLabel =
                                            laneType === 'advanced'
                                              ? 'Advanced lane'
                                              : laneType === 'specialty'
                                                ? 'Specialty lane'
                                                : 'Core lane';
                                          return renderOfferingSelectionCard(
                                            offering,
                                            index,
                                            laneLabel,
                                            'service lane',
                                          );
                                        })}
                                      </SelectionGrid>
                                    </OfferingTypeSection>
                                  ))
                                ) : (
                                  <SelectionGrid>
                                    {displayOfferings.map((offering, index) =>
                                      renderOfferingSelectionCard(
                                        offering,
                                        index,
                                        'Offering',
                                        'offering',
                                      ),
                                    )}
                                  </SelectionGrid>
                                )}
                                <div
                                  style={{
                                    color: '#9ca6b6',
                                    fontSize: 11,
                                    lineHeight: 1.55,
                                    fontWeight: 700,
                                  }}
                                >
                                  {offeringStatusCopy}
                                </div>
                              </>
                            ) : (
                              <Notice $tone="good">
                                This tier does not require any offering selections, so the flow is
                                ready to continue.
                              </Notice>
                            )}
                          </FlatStep>
                        ) : null}
                        {showDiscountStep ? <FlatStep key="discount-step" layout $dimmed={focusedStep !== 'discount' && discountStepComplete} onClick={() => setFocusedStep('discount')} initial={reduceMotion ? false : { opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.18 }}><StepText><StepKicker>Step 3</StepKicker><StepTitle>Coupon code or discounts</StepTitle><StepCopy>Apply any discount context before purchase. Accounts carrying two or more active primary services receive a 15% bundled discount automatically.</StepCopy></StepText>{calculatedOrder?.discountCents ? <ServiceBanner $theme={theme}><div style={{ fontSize: 11, fontWeight: 900, letterSpacing: '.18px', textTransform: 'uppercase' }}>Bundle discount unlocked</div><div style={{ fontSize: 13, lineHeight: 1.55, fontWeight: 800 }}>{formatCurrency(calculatedOrder.discountCents)} off recurring service charges because this account will carry two or more active primary services.</div></ServiceBanner> : null}<div style={{ display: 'grid', gap: 10 }}><div style={{ display: 'grid', gap: 12, alignItems: 'end', gridTemplateColumns: 'minmax(0, 1fr) auto' }}><div style={{ display: 'grid', gap: 6 }}><Eyebrow>Coupon code</Eyebrow><Input value={discountCode} onChange={(event) => { event.stopPropagation(); setDiscountCode(event.target.value); setDiscountStepComplete(false); }} placeholder="Add code if you have one" /></div><PrimaryButton type="button" style={{ minHeight: 44 }} onClick={(event) => { event.stopPropagation(); setDiscountStepComplete(true); }}>Apply<ArrowRight size={14} /></PrimaryButton></div><SecondaryButton type="button" onClick={(event) => { event.stopPropagation(); setDiscountCode(''); setDiscountStepComplete(true); }}>Skip</SecondaryButton></div><div style={{ color: '#9ca6b6', fontSize: 11, lineHeight: 1.55, fontWeight: 700 }}>Coupon codes are validated at checkout. The live summary below already reflects the bundle discount when it is unlocked.</div></FlatStep> : null}
                      </AnimatePresence>
                  </div>
                </Main>
                <Rail>
                  <RailCardWrap>
                    <ServiceCard service={selectedService} currentService={currentService} currentTier={currentTier} selectedTier={selectedTier} selectedOfferingLabels={selectedOfferingLabels.length ? selectedOfferingLabels : currentService?.selectedOfferings ?? []} availableOfferingCount={displayOfferings.length} price={calculatedOrder ?? currentServicePrice} mode="detail" theme={theme} layoutId={`service-card-${selectedServiceKey}`} />
                  </RailCardWrap>
                  <AnimatePresence initial={false}>
                    {canCheckoutSelection ? (
                      <RailPurchaseWrap
                        key="rail-buy"
                        initial={reduceMotion ? false : { opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
                        transition={{ duration: 0.18, ease: 'easeOut' }}
                      >
                        <RailPurchaseButton
                          type="button"
                          disabled={checkoutBusy}
                          onClick={(event) => {
                            event.stopPropagation();
                            setPurchaseModalOpen(true);
                          }}
                        >
                          {checkoutBusy
                            ? isProposalMode
                              ? 'Sending proposal...'
                              : 'Starting checkout...'
                            : railActionLabel}
                          <ArrowRight size={14} />
                        </RailPurchaseButton>
                        <RailPurchaseMeta>
                          {railActionMeta}
                        </RailPurchaseMeta>
                      </RailPurchaseWrap>
                    ) : currentService && discountUnlocked && canManageServices && railIdleNote ? (
                      <RailPurchaseWrap
                        key="rail-buy-note"
                        initial={reduceMotion ? false : { opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
                        transition={{ duration: 0.18, ease: 'easeOut' }}
                      >
                        <RailPurchaseMeta>
                          {railIdleNote}
                        </RailPurchaseMeta>
                      </RailPurchaseWrap>
                    ) : null}
                  </AnimatePresence>
                </Rail>
              </DetailView>
              {isMobileSnapshotMode &&
              !mobileSnapshotOpen &&
              !mobileOfferingDrawer ? (
                <MobileSnapshotPill
                  type="button"
                  $theme={theme}
                  onClick={() => setMobileSnapshotOpen(true)}
                  aria-label="Open service snapshot"
                >
                  <FileText size={14} />
                  Snapshot
                </MobileSnapshotPill>
              ) : null}
              <AnimatePresence>
                {isMobileSnapshotMode && mobileSnapshotOpen ? (
                  <>
                    <MobileSnapshotOverlay
                      type="button"
                      aria-label="Close service snapshot"
                      onClick={() => setMobileSnapshotOpen(false)}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.16 }}
                    />
                    <MobileSnapshotDrawer
                      initial={reduceMotion ? false : { x: '100%' }}
                      animate={{ x: 0 }}
                      exit={reduceMotion ? { opacity: 0 } : { x: '100%' }}
                      transition={{ duration: 0.22, ease: 'easeOut' }}
                    >
                      <MobileSnapshotDrawerHeader>
                        <MobileSnapshotDrawerTitle>Service snapshot</MobileSnapshotDrawerTitle>
                        <MobileSnapshotDrawerClose
                          type="button"
                          aria-label="Close service snapshot"
                          onClick={() => setMobileSnapshotOpen(false)}
                        >
                          <X size={16} />
                        </MobileSnapshotDrawerClose>
                      </MobileSnapshotDrawerHeader>
                      <MobileSnapshotDrawerScroll>
                        <MobileSnapshotCardWrap>
                          <ServiceCard service={selectedService} currentService={currentService} currentTier={currentTier} selectedTier={selectedTier} selectedOfferingLabels={selectedOfferingLabels.length ? selectedOfferingLabels : currentService?.selectedOfferings ?? []} availableOfferingCount={displayOfferings.length} price={calculatedOrder ?? currentServicePrice} mode="detail" theme={theme} layoutId={`service-card-mobile-${selectedServiceKey}`} />
                        </MobileSnapshotCardWrap>
                        {canCheckoutSelection ? (
                          <RailPurchaseWrap>
                            <RailPurchaseButton
                              type="button"
                              disabled={checkoutBusy}
                              onClick={(event) => {
                                event.stopPropagation();
                                setPurchaseModalOpen(true);
                              }}
                            >
                              {checkoutBusy
                                ? isProposalMode
                                  ? 'Sending proposal...'
                                  : 'Starting checkout...'
                                : railActionLabel}
                              <ArrowRight size={14} />
                            </RailPurchaseButton>
                            <RailPurchaseMeta>
                              {railActionMeta}
                            </RailPurchaseMeta>
                          </RailPurchaseWrap>
                        ) : currentService && discountUnlocked && canManageServices && railIdleNote ? (
                          <RailPurchaseWrap>
                            <RailPurchaseMeta>
                              {railIdleNote}
                            </RailPurchaseMeta>
                          </RailPurchaseWrap>
                        ) : null}
                      </MobileSnapshotDrawerScroll>
                    </MobileSnapshotDrawer>
                  </>
                ) : null}
              </AnimatePresence>
              <AnimatePresence>
                {isMobileSnapshotMode && mobileOfferingDrawer ? (
                  <>
                    <MobileOfferingOverlay
                      type="button"
                      aria-label="Close service lane details"
                      onClick={() => setMobileOfferingDrawer(null)}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.16 }}
                    />
                    <MobileOfferingDrawer
                      initial={reduceMotion ? false : { y: '100%' }}
                      animate={{ y: 0 }}
                      exit={reduceMotion ? { opacity: 0 } : { y: '100%' }}
                      transition={{ duration: 0.24, ease: 'easeOut' }}
                    >
                      <MobileOfferingDrawerHeader>
                        <MobileOfferingDrawerHandle />
                        <MobileOfferingDrawerTopRow>
                          <MobileOfferingDrawerHeaderMeta>
                            <MobileOfferingDrawerIndicator
                              $active={mobileOfferingDrawerActive}
                            >
                              {mobileOfferingDrawerActive ? (
                                <Check size={11} />
                              ) : null}
                            </MobileOfferingDrawerIndicator>
                            <MobileOfferingDrawerEyebrow>
                              {mobileOfferingDrawer.eyebrow}
                            </MobileOfferingDrawerEyebrow>
                            <MobileOfferingDrawerStatus>
                              {mobileOfferingDrawerStatus}
                            </MobileOfferingDrawerStatus>
                          </MobileOfferingDrawerHeaderMeta>
                        </MobileOfferingDrawerTopRow>
                      </MobileOfferingDrawerHeader>
                      <MobileOfferingDrawerScroll>
                        <MobileOfferingDrawerSummary>
                          <MobileOfferingDrawerTitle>
                            {cleanText(
                              mobileOfferingDrawer.offering.name ||
                                mobileOfferingDrawer.offering.code ||
                                mobileOfferingDrawer.offering.id,
                            ) || 'Offering'}
                          </MobileOfferingDrawerTitle>
                          <MobileOfferingDrawerCopy>
                            {mobileOfferingDrawerCopy}
                          </MobileOfferingDrawerCopy>
                        </MobileOfferingDrawerSummary>
                        <MobileOfferingActionButton
                          type="button"
                          disabled={mobileOfferingActionDisabled}
                          onClick={handleMobileOfferingAction}
                        >
                          {mobileOfferingActionLabel}
                        </MobileOfferingActionButton>
                      </MobileOfferingDrawerScroll>
                    </MobileOfferingDrawer>
                  </>
                ) : null}
              </AnimatePresence>
              </React.Fragment>
            )}
          </AnimatePresence>
          <AnimatePresence>
            {purchaseModalOpen && checkoutPayload ? (
              <>
                <ModalBackdrop
                  key="purchase-modal-backdrop"
                  type="button"
                  aria-label="Close purchase confirmation"
                  onClick={() => {
                    if (purchaseModalPhase === 'review') {
                      setPurchaseModalOpen(false);
                    }
                  }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.16 }}
                />
                <ModalViewport>
                  <ModalPanel
                    key="purchase-modal-panel"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="service-purchase-title"
                    initial={reduceMotion ? false : { opacity: 0, y: 18, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -12, scale: 0.98 }}
                    transition={{ duration: 0.18, ease: 'easeOut' }}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <ModalHeader>
                      <ModalHeaderText>
                        <ModalKicker>
                          {isProposalMode ? 'Proposal review' : 'Checkout review'}
                        </ModalKicker>
                        <ModalTitle id="service-purchase-title">{modalTitleText}</ModalTitle>
                        <ModalSub>{modalSubText}</ModalSub>
                      </ModalHeaderText>
                    </ModalHeader>

                    <AnimatePresence mode="wait" initial={false}>
                      {purchaseModalPhase === 'review' ? (
                        <motion.div
                          key="modal-review"
                          initial={reduceMotion ? false : { opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -10 }}
                          transition={{ duration: 0.18, ease: 'easeOut' }}
                          style={{ display: 'grid', gap: 18 }}
                        >
                    <EstimateHero>
                      <EstimateHeroLead>
                        <EstimateHeroLabel>{modalHeroLabel}</EstimateHeroLabel>
                        {showTypingHeroValue ? (
                          <TypingEstimateHeroValue $reducedMotion={Boolean(reduceMotion)}>
                            {modalHeroValue}
                          </TypingEstimateHeroValue>
                        ) : (
                          <EstimateHeroValue>{modalHeroValue}</EstimateHeroValue>
                        )}
                        <EstimateHeroCopy>{modalHeroCopy}</EstimateHeroCopy>
                      </EstimateHeroLead>
                      <EstimateHeroMetrics>
                        <EstimateHeroMetric>
                          <EstimateHeroMetricLabel>
                            Projected monthly
                          </EstimateHeroMetricLabel>
                          <EstimateHeroMetricValue>
                            {modalProjectedMonthlyValue}
                          </EstimateHeroMetricValue>
                        </EstimateHeroMetric>
                        <EstimateHeroMetric>
                          <EstimateHeroMetricLabel>Discounts</EstimateHeroMetricLabel>
                          <EstimateHeroMetricValue>
                            {modalDiscountValue}
                          </EstimateHeroMetricValue>
                        </EstimateHeroMetric>
                      </EstimateHeroMetrics>
                    </EstimateHero>

                    <ModalGrid $singleColumn={isProposalMode || !showPaymentPanel}>
                      <Receipt>
                        <PurchaseBlockTitle>Configuration</PurchaseBlockTitle>
                        <PurchaseDetails>
                          <PurchaseCell>
                            <PurchaseLabel>Service</PurchaseLabel>
                            <PurchaseValue>{cleanText(selectedService?.title) || 'Service'}</PurchaseValue>
                          </PurchaseCell>
                          <PurchaseCell>
                            <PurchaseLabel>{isFixedSubscriptionService ? 'Subscription' : 'Tier'}</PurchaseLabel>
                            <PurchaseValue>{cleanText(selectedTier?.name) || '--'}</PurchaseValue>
                          </PurchaseCell>
                          <PurchaseWideCell>
                            <PurchaseLabel>{isFixedSubscriptionService ? 'Delivery model' : 'Selected offerings'}</PurchaseLabel>
                            {isFixedSubscriptionService || !selectedOfferingLabels.length ? (
                              <PurchaseValue>{checkoutOfferingSummary}</PurchaseValue>
                            ) : (
                              <OfferingList>
                                {selectedOfferingLabels.map((label) => (
                                  <OfferingListItem key={label}>{label}</OfferingListItem>
                                ))}
                              </OfferingList>
                            )}
                          </PurchaseWideCell>
                        </PurchaseDetails>
                        {serviceAction === 'upgrade' && upgradePreviewError ? (
                          <Notice $tone="warn">{upgradePreviewError}</Notice>
                        ) : null}
                        {purchaseModalError ? (
                          <Notice $tone="warn">{purchaseModalError}</Notice>
                        ) : null}
                        {!isProposalMode && cleanText(discountCode) ? (
                          <Notice>
                            Coupon code <strong>{discountCode.trim()}</strong> will be validated during secure checkout.
                          </Notice>
                        ) : null}
                      </Receipt>

                      {showPaymentPanel ? <PaymentCard>
                        <PurchaseBlockTitle>Payment method on file</PurchaseBlockTitle>
                        {!modalRequiresPaymentMethod ? (
                          <Notice $tone="good">
                            No payment method is required for this update. Confirming will apply the new tier and offering selections to the active service immediately.
                          </Notice>
                        ) : paymentMethodsLoading && paymentMethods.length === 0 ? (
                          <Notice>Loading payment methods for this account...</Notice>
                        ) : paymentMethodsError ? (
                          <Notice $tone="warn">{paymentMethodsError}</Notice>
                        ) : primaryPaymentMethod ? (
                          <>
                            <PaymentVisual>
                              <PaymentVisualGlow />
                              <PaymentVisualTop>
                                <PaymentStatus>
                                  {primaryPaymentMethod.isDefault ? 'Default card' : 'Saved card'}
                                </PaymentStatus>
                              </PaymentVisualTop>
                              <div style={{ display: 'grid', gap: 16, position: 'relative', zIndex: 1 }}>
                                <PaymentBrand>{formatPaymentBrand(primaryPaymentMethod.brand)}</PaymentBrand>
                                <PaymentDigits>
                                  •••• •••• •••• {primaryPaymentMethod.last4 || '----'}
                                </PaymentDigits>
                                <PaymentVisualMeta>
                                  <PaymentMetaItem>
                                    <PaymentMetaLabel>Cardholder</PaymentMetaLabel>
                                    <PaymentMetaValue>Primary account holder</PaymentMetaValue>
                                  </PaymentMetaItem>
                                  <PaymentMetaItem>
                                    <PaymentMetaLabel>Expires</PaymentMetaLabel>
                                    <PaymentMetaValue>
                                      {primaryPaymentMethod.exp_month || '--'}/{primaryPaymentMethod.exp_year || '--'}
                                    </PaymentMetaValue>
                                  </PaymentMetaItem>
                                </PaymentVisualMeta>
                              </div>
                            </PaymentVisual>
                          </>
                        ) : (
                          <>
                            <Notice $tone="warn">
                              No payment method is currently saved on this account. Add one first before confirming this purchase.
                            </Notice>
                            <ModalActions style={{ justifyContent: 'flex-start' }}>
                              <PrimaryButton
                                type="button"
                                onClick={() => {
                                  setPurchaseModalOpen(false);
                                  navigate('/dashboard/billing');
                                }}
                              >
                                Add payment method
                              </PrimaryButton>
                            </ModalActions>
                          </>
                        )}
                      </PaymentCard> : null}
                    </ModalGrid>

                    <ModalActions>
                      <ModalActionMeta>{modalFooterMeta}</ModalActionMeta>
                      <PrimaryButton
                        type="button"
                        style={{ minHeight: 46, padding: '0 18px' }}
                        disabled={
                          checkoutBusy ||
                          (serviceAction === 'upgrade' &&
                            !proposalUpgradeEstimateDeferred &&
                            (upgradePreviewLoading ||
                              Boolean(upgradePreviewError) ||
                              !upgradePreview)) ||
                          (modalRequiresPaymentMethod &&
                            (paymentMethodsLoading || !primaryPaymentMethod))
                        }
                        onClick={() => {
                          void handleModalConfirm();
                        }}
                      >
                        {checkoutBusy
                          ? isProposalMode
                            ? 'Sending proposal...'
                            : 'Starting checkout...'
                          : modalConfirmText}
                        <ArrowRight size={14} />
                      </PrimaryButton>
                    </ModalActions>
                        </motion.div>
                      ) : purchaseModalPhase === 'processing' ? (
                        <ModalStateWrap
                          key="modal-processing"
                          initial={reduceMotion ? false : { opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -10 }}
                          transition={{ duration: 0.18, ease: 'easeOut' }}
                        >
                          <ModalStateIcon>
                            <ModalSpinner />
                          </ModalStateIcon>
                          <div style={{ display: 'grid', gap: 8 }}>
                            <ModalStateTitle>{processingStateTitle}</ModalStateTitle>
                            <ModalStateCopy>{processingStateCopy}</ModalStateCopy>
                          </div>
                        </ModalStateWrap>
                      ) : (
                        <ModalStateWrap
                          key="modal-success"
                          initial={reduceMotion ? false : { opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -10 }}
                          transition={{ duration: 0.18, ease: 'easeOut' }}
                        >
                          <ModalStateIcon $success>
                            <Check size={28} />
                          </ModalStateIcon>
                          <div style={{ display: 'grid', gap: 8 }}>
                            <ModalStateTitle>{successStateTitle}</ModalStateTitle>
                            <ModalStateCopy>{successStateCopy}</ModalStateCopy>
                          </div>
                        </ModalStateWrap>
                      )}
                    </AnimatePresence>
                  </ModalPanel>
                </ModalViewport>
              </>
            ) : null}
          </AnimatePresence>
        </Viewport>
      </Shell>
    </LayoutGroup>
  );
}
